import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Carousel from "../components/Carousel";
import SEO from "../components/SEO";
import MetaAdLibrarySection from "../components/MetaAdLibrarySection";
import Clients from "../components/Clients";
import Videos from "../components/Videos";
import DMShowcase from "../components/DMShowcase";
import axios from "../lib/axios";
import { useAuth } from "../authContext";
import { useCart } from "../cartContext";
import RegisterModal from "../Modals/RegisterModal";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCheck,
  FaCamera,
  FaVideo,
  FaMicrophone,
  FaMinus,
  FaPlus,
  FaGoogle,
  FaWhatsapp,
  FaArrowRight,
  FaShieldAlt,
  FaReceipt,
  FaEnvelope,
  FaComments,
  FaStar,
  FaGlobe,
} from "react-icons/fa";
import { FaInstagram, FaMeta, FaCartShopping } from "react-icons/fa6";
import {
  LuX,
  LuArrowDown,
  LuChevronUp,
  LuImage,
  LuFilm,
  LuBot,
  LuLayers,
  LuLayoutGrid,
  LuSparkles,
  LuCheck,
} from "react-icons/lu";
import { TbDrone } from "react-icons/tb";
import "../CSS/dm-editorial.css";
import { fallbackOtherServices } from "./DMServiceDetail";

/* ═══════════════════════════════════════════════════════════════════
   FALLBACK DATA — used when the admin global isn't configured yet.
   All of this can be overridden from the Digital Marketing global
   in the admin panel.
   ═══════════════════════════════════════════════════════════════════ */

const fallbackHeroStats = [
  { value: "500+", label: "No. of Customers" },
  { value: "1,200+", label: "No. of Campaigns" },
  { value: "8+", label: "No. of years" },
  { value: "Meta & Google", label: "No. of Certifications" },
];

const fallbackDurations = [
  { months: 1, label: "1 Month", discount_percentage: 0, badge_text: "Standard", is_default: true },
  { months: 3, label: "3 Month", discount_percentage: 10, badge_text: "10% OFF", is_default: false },
  { months: 6, label: "6 Month", discount_percentage: 20, badge_text: "20% OFF", is_default: false },
  { months: 12, label: "12 Month", discount_percentage: 30, badge_text: "30% OFF", is_default: false },
];

const fallbackContentTypes = [
  {
    id: "image_post",
    name: "Image Post",
    icon: "image",
    unit: "post",
    description: "Custom brand creatives, offer posters & single-image announcements.",
    youtube_video_url: "https://www.youtube.com/watch?v=kYJv5_5Q4c4",
    specifications: "1080×1080 & 1080×1350 px • High-Res PNG/JPG • Editable source link",
    is_recommended: false,
    info_points: [
      "Tailored brand design with custom color palette & typography",
      "High-resolution export for Instagram, Facebook & LinkedIn",
      "Engaging marketing caption & researched hashtag bundle",
      "Up to 2 creative revision rounds per design",
      "Delivered ready-to-post on your schedule",
    ],
    prices: [
      { months: 1, price: 120 },
      { months: 3, price: 110 },
      { months: 6, price: 100 },
      { months: 12, price: 85 },
    ],
  },
  {
    id: "motion_poster",
    name: "Motion Poster",
    icon: "motion",
    unit: "pc",
    description: "Animated graphics with smooth motion, kinetic typography & visual effects.",
    youtube_video_url: "https://www.youtube.com/watch?v=Jb1b8X_2iEQ",
    specifications: "1080×1350 & 1080×1920 px • 60 FPS MP4 • Audio Synced",
    is_recommended: false,
    info_points: [
      "Kinetic typography, animated brand elements & transition effects",
      "Royalty-free trending background music beat-sync",
      "Designed to stop thumb scrolling on Instagram feeds & stories",
      "Up to 2 revision rounds per motion piece",
    ],
    prices: [
      { months: 1, price: 250 },
      { months: 3, price: 230 },
      { months: 6, price: 210 },
      { months: 12, price: 180 },
    ],
  },
  {
    id: "ai_monologue_video",
    name: "AI & Monologue Video",
    icon: "ai_video",
    unit: "reel",
    description: "AI-generated avatar or scripted monologue videos built for high retention.",
    youtube_video_url: "https://www.youtube.com/watch?v=eYkHkPZ4_2A",
    specifications: "9:16 Vertical Video (1080×1920) • Auto-Captions • Color Graded",
    is_recommended: true,
    info_points: [
      "High-converting viral scriptwriting & storytelling hook creation",
      "Ultra-realistic AI avatar or custom brand voiceover synthesis",
      "Dynamic pop-up animated word-by-word subtitles with emoji callouts",
      "B-roll overlays, visual cues, sound effects & trending audio",
      "Delivered in vertical 9:16 format ready for Reels & Shorts",
    ],
    prices: [
      { months: 1, price: 450 },
      { months: 3, price: 420 },
      { months: 6, price: 390 },
      { months: 12, price: 340 },
    ],
  },
  {
    id: "reels",
    name: "Reels",
    icon: "reels",
    unit: "reel",
    description: "High-paced reel editing, trending transitions, sound design & viral pacing.",
    youtube_video_url: "https://www.youtube.com/watch?v=9g0s2sR6u0g",
    specifications: "1080×1920 (9:16) • 4K/1080p • SFX & LUT Color Grading",
    is_recommended: false,
    info_points: [
      "Pacing & fast-cut editing from your raw footage or on-ground shoot",
      "Trending audio research & beat-matched cut transitions",
      "Animated captions with icon highlights & sound effects (SFX)",
      "Cinematic color correction & background noise cleanup",
      "Fast 48-hour turnaround per reel",
    ],
    prices: [
      { months: 1, price: 1499 },
      { months: 3, price: 1399 },
      { months: 6, price: 1299 },
      { months: 12, price: 1199 },
    ],
  },
  {
    id: "carousel_post",
    name: "Carousel Post",
    icon: "carousel",
    unit: "post",
    description: "Multi-slide storytelling carousels that drive saves, shares & engagement.",
    youtube_video_url: "https://www.youtube.com/watch?v=yW6hV6eL1sA",
    specifications: "Up to 5–8 swipeable slides • Seamless continuous banner format",
    is_recommended: false,
    info_points: [
      "Educational, storytelling, or listicle slide sequence",
      "Seamless panoramic flow between slides for fluid swiping",
      "Attention-grabbing hook slide + high-converting end CTA slide",
      "Detailed caption copywriting with targeted hashtag bundle",
      "2 revision rounds included",
    ],
    prices: [
      { months: 1, price: 350 },
      { months: 3, price: 320 },
      { months: 6, price: 290 },
      { months: 12, price: 250 },
    ],
  },
  {
    id: "grid_post",
    name: "Grid Post (3 in a row)",
    icon: "grid",
    unit: "grid",
    description: "A unified 3-post panoramic banner that transforms your profile feed aesthetics.",
    youtube_video_url: "https://www.youtube.com/watch?v=t1e8g93i_A0",
    specifications: "3-Tile horizontal panoramic split (3240×1080 px) • Instagram feed ready",
    is_recommended: false,
    info_points: [
      "Interconnected 3-tile panoramic artwork for your Instagram profile",
      "High-impact visual branding for product launches or milestones",
      "Numbered posting guide for flawless sequential publishing",
      "Captions & hashtags for all 3 posts included",
    ],
    prices: [
      { months: 1, price: 450 },
      { months: 3, price: 420 },
      { months: 6, price: 390 },
      { months: 12, price: 340 },
    ],
  },
];

const fallbackManagement = [
  {
    id: "base",
    name: "Foundation Plan",
    monthly_price: 4999,
    description: "Essential brand presence, daily scheduled social publishing & monthly growth analytics.",
    points: [
      "Multi-Platform Publishing (Instagram & Facebook)",
      "Daily Story Updates & Custom Highlight Covers",
      "Sharing in 3 Targeted Community / FB Groups",
      "Monthly Reach, Impression & Follower Analytics",
    ],
    period_prices: [
      { months: 1, price: 4999 },
      { months: 3, price: 13497 },
      { months: 6, price: 25495 },
      { months: 12, price: 44991 },
    ],
    is_recommended: false,
  },
  {
    id: "advanced",
    name: "Growth Plan",
    monthly_price: 8999,
    description: "Full proactive social growth, viral hook strategy & dedicated account manager.",
    points: [
      "Everything in Foundation Plan",
      "Proactive DM & Comment Engagement (Fast Replies)",
      "Viral Hook & Trending Audio Research Strategy",
      "Competitor Benchmarking & Dedicated Manager",
    ],
    period_prices: [
      { months: 1, price: 8999 },
      { months: 3, price: 24297 },
      { months: 6, price: 45895 },
      { months: 12, price: 80991 },
    ],
    is_recommended: true,
  },
];

const fallbackAds = {
  meta: {
    enabled: true,
    name: "Meta Ads Campaign (Instagram & Facebook)",
    monthly_price: 2500,
    description:
      "Pixel & Conversion API setup, custom & lookalike audience funnels, A/B creative testing & ROAS optimization.",
    period_prices: [
      { months: 1, price: 2500 },
      { months: 3, price: 6750 },
      { months: 6, price: 12000 },
      { months: 12, price: 21000 },
    ],
  },
  google: {
    enabled: true,
    name: "Google Ads Campaign (Search, YouTube & Display)",
    monthly_price: 2000,
    description:
      "High-intent search keyword bidding, negative keyword filters, conversion tracking & smart bid management.",
    period_prices: [
      { months: 1, price: 2000 },
      { months: 3, price: 5400 },
      { months: 6, price: 9600 },
      { months: 12, price: 16800 },
    ],
  },
};

const fallbackShoots = [
  {
    id: "photoshoot",
    name: "Photography Shoot",
    price: 4999,
    duration: "Up to 2–3 Hours",
    icon: "camera",
    description: "Professional on-ground photography with pro camera, lighting and direction at your location in Indore.",
    specifications: "Full-frame camera • Basic lighting • 1 location • High-res delivery",
    youtube_video_url: "",
    info_modal_title: "Deliverables & Scope",
    info_section_heading: "What we are providing:",
    info_points: [
      "Professional camera",
      "Basic lighting setup",
      "1 location in Indore",
      "Photography & raw/selected photos delivery",
    ],
    inclusions: [
      "Professional camera",
      "Basic lighting setup",
      "1 location in Indore",
      "Photography & raw/selected photos delivery",
    ],
    note: "Photo retouching/editing charged separately. Additional hours/locations extra.",
    period_prices: [
      { months: 1, price: 4999 },
      { months: 3, price: 4499 },
      { months: 6, price: 4249 },
      { months: 12, price: 3749 },
    ],
  },
  {
    id: "reel_shoot",
    name: "Reel / Video Shoot",
    price: 7499,
    duration: "Up to 2–3 Hours",
    icon: "video",
    description: "Dynamic reel/video shoot with direction for hooks, 4K capture and clean audio for Reels & ads.",
    specifications: "4K capture • Lighting • Direction • Raw footage delivery",
    youtube_video_url: "",
    info_modal_title: "Deliverables & Scope",
    info_section_heading: "What we are providing:",
    info_points: [
      "Professional camera setup",
      "Basic lighting setup",
      "1 shooting location",
      "Shooting & direction",
      "Raw footage delivery",
    ],
    inclusions: [
      "Professional camera setup",
      "Basic lighting setup",
      "1 shooting location",
      "Shooting & direction",
      "Raw footage delivery",
    ],
    note: "Editing charged separately. Extra locations/hours, models/actors extra.",
    period_prices: [
      { months: 1, price: 7499 },
      { months: 3, price: 6749 },
      { months: 6, price: 6374 },
      { months: 12, price: 5624 },
    ],
  },
  {
    id: "podcast_shoot",
    name: "Podcast Shoot Session",
    price: 9999,
    duration: "Up to 2–3 Hours",
    icon: "mic",
    description: "Broadcast-standard multi-camera podcast recording with studio-grade audio and lighting.",
    specifications: "2-camera • Pro audio • Lighting • Multi-track delivery",
    youtube_video_url: "",
    info_modal_title: "Deliverables & Scope",
    info_section_heading: "What we are providing:",
    info_points: [
      "2-Camera multi-angle setup",
      "Professional audio recording",
      "Basic lighting & multi-angle capture",
      "Raw multi-track audio & video delivery",
    ],
    inclusions: [
      "2-Camera multi-angle setup",
      "Professional audio recording",
      "Basic lighting & multi-angle capture",
      "Raw multi-track audio & video delivery",
    ],
    note: "Editing charged separately. Studio/location rental extra if required.",
    period_prices: [
      { months: 1, price: 9999 },
      { months: 3, price: 8999 },
      { months: 6, price: 8499 },
      { months: 12, price: 7499 },
    ],
  },
  {
    id: "drone_shoot",
    name: "Drone Shoot",
    price: 7499,
    duration: "Up to 1–2 Hours",
    icon: "drone",
    description: "Cinematic 4K aerial photography & videography with licensed operator and flight planning.",
    specifications: "4K aerial • Licensed operator • Flight planning • Raw delivery",
    youtube_video_url: "",
    info_modal_title: "Deliverables & Scope",
    info_section_heading: "What we are providing:",
    info_points: [
      "Professional drone operator",
      "4K Aerial photography/videography",
      "Basic flight planning & raw footage",
    ],
    inclusions: [
      "Professional drone operator",
      "4K Aerial photography/videography",
      "Basic flight planning & raw footage",
    ],
    note: "Weather-dependent. Flight permissions and outstation travel extra.",
    period_prices: [
      { months: 1, price: 7499 },
      { months: 3, price: 6749 },
      { months: 6, price: 6374 },
      { months: 12, price: 5624 },
    ],
  },
];

const budgetPresets = [
  { daily: 500, label: "₹500/day" },
  { daily: 1000, label: "₹1,000/day" },
  { daily: 2500, label: "₹2,500/day" },
  { daily: 5000, label: "₹5,000/day" },
  { daily: 10000, label: "₹10,000/day" },
];

const fallbackCoupons = [
  { code: "INDORI18", discount_percentage: 18, label: "18% OFF", is_active: true },
];

// Desired Other Services display order: 1 WhatsApp, 2 Google Data Extractor,
// 3 Bulk SMS, 4 Static Website, 5 Influencer, 6 Email (mail).
const OTHER_SERVICES_ORDER = [
  "whatsapp",
  "google-data-extractor",
  "sms",
  "static-website",
  "influencer",
  "mail",
];

const formatRupees = (num) => Number(num || 0).toLocaleString("en-IN");

const easeOut = [0.22, 1, 0.36, 1];

const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

const heroItem = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: easeOut } },
};

/* ─── Modern Icon Component for Content Types ─── */

/* ─── Official Instagram Reels SVG Icon ─── */
export const InstagramReelsIcon = ({ className = "text-[#bc1535]", size = 16, height = 16, width = 16, style, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width={width || size}
    height={height || size}
    style={{ width: width || size, height: height || size, minWidth: width || size, minHeight: height || size, ...style }}
    className={`inline-block shrink-0 ${className}`}
    aria-hidden="true"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.552 2C5.704 2 4.546 2.699 3.606 3.607 2.698 4.546 2 5.704 2 8.552v6.898c0 2.849.698 4.006 1.606 4.945.94.908 2.098 1.605 4.946 1.605h6.896c2.848 0 4.006-.697 4.946-1.605.908-.939 1.606-2.096 1.606-4.945V8.552c0-2.848-.698-4.006-1.606-4.945C20.454 2.699 19.296 2 16.448 2H8.552ZM4 8h16v7.45c0 2.2-.54 3.08-1.25 3.81-.73.71-1.61 1.24-3.81 1.24H9.06c-2.2 0-3.08-.53-3.81-1.24C4.54 18.53 4 17.65 4 15.45V8Zm5.31-4H6.38l-1.6 2h2.93l1.6-2Zm2.55 0h-1.94l-1.6 2h1.94l1.6-2Zm3.84 0h-1.94l-1.6 2h1.94l1.6-2Zm3.92 0h-2.02l-1.6 2h2.72l.9-1.12c-.22-.38-.48-.66-.9-.88ZM9.31 11.63v5.25a.91.91 0 0 0 1.36.79l4.55-2.63a.91.91 0 0 0 0-1.57l-4.55-2.63a.91.91 0 0 0-1.36.79Z"
    />
  </svg>
);

/* ─── YouTube Video Embed URL Helper ─── */
export const getYouTubeEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.includes("/embed/")) {
    // Normalize existing embed URLs to include rel=0
    return trimmed.includes("?") ? trimmed : `${trimmed}?rel=0`;
  }
  // Plain 11-char video ID pasted directly in admin
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return `https://www.youtube.com/embed/${trimmed}?rel=0`;
  }
  const shortsMatch = trimmed.match(/youtube\.com\/(shorts|live)\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch && shortsMatch[2]) {
    return `https://www.youtube.com/embed/${shortsMatch[2]}?rel=0`;
  }
  const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch && shortMatch[1]) {
    return `https://www.youtube.com/embed/${shortMatch[1]}?rel=0`;
  }
  const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]+)/);
  if (watchMatch && watchMatch[1]) {
    return `https://www.youtube.com/embed/${watchMatch[1]}?rel=0`;
  }
  return null;
};

const ContentTypeIcon = ({ typeKey, className = "text-base text-[#bc1535]" }) => {
  switch (typeKey) {
    case "image_post":
    case "static_image":
    case "image":
      return <LuImage className={className} />;
    case "motion_poster":
    case "motion":
      return <LuFilm className={className} />;
    case "ai_monologue_video":
    case "ai_video":
    case "ai":
      return <LuBot className={className} />;
    case "reels":
    case "reel_edit":
      return <InstagramReelsIcon className={className} />;
    case "carousel_post":
    case "carousel":
      return <LuLayers className={className} />;
    case "grid_post":
    case "grid":
      return <LuLayoutGrid className={className} />;
    default:
      return <LuSparkles className={className} />;
  }
};

/* ─── Shoot Icon Helper ─── */
const ShootIcon = ({ icon }) => {
  switch (icon) {
    case "camera":
    case "photoshoot":
      return <FaCamera className="text-base" />;
    case "video":
    case "reel_shoot":
      return <FaVideo className="text-base" />;
    case "mic":
    case "podcast_shoot":
      return <FaMicrophone className="text-base" />;
    case "drone":
    case "drone_shoot":
      return <TbDrone className="text-lg" />;
    default:
      return <FaCamera className="text-base" />;
  }
};

/* ─── Normalizers ─────────────────────────────────────────── */

const normalizeDurations = (list) => {
  if (Array.isArray(list) && list.length) {
    return list.map((d) => ({
      months: Number(d.months ?? 1),
      label: d.label || `${d.months ?? 1} Mo`,
      discount_percentage: Number(d.discount_percentage || 0),
      badge_text: d.badge_text || null,
      is_default: Boolean(d.is_default),
    }));
  }
  return fallbackDurations;
};

const normalizeContentTypes = (list) => {
  const parsePoints = (raw, fbPoints) => {
    if (Array.isArray(raw) && raw.length > 0) {
      const parsed = raw
        .map((p) => {
          if (typeof p === "string") return p.trim();
          if (p == null) return "";
          return String(
            p.point ?? p.item ?? p.text ?? p.title ?? p.value ?? p.description ?? p.inclusion ?? ""
          ).trim();
        })
        .filter(Boolean);
      // Admin explicitly set points (even if cleared to empty, fall back
      // to inclusions so the (i) popup never goes blank unexpectedly)
      if (parsed.length > 0) return parsed;
    }
    return fbPoints || [];
  };
  if (Array.isArray(list) && list.length) {
    return list.map((c, i) => {
      const fb =
        fallbackContentTypes.find((f) => f.id === c.id) ||
        fallbackContentTypes[i] ||
        {};
      return {
        id: c.id || fb.id || `type_${i}`,
        name: c.name || fb.name || "Content",
        icon: c.icon || fb.icon || "image",
        unit: c.unit || fb.unit || "post",
        description: c.description ?? fb.description ?? "",
        youtube_video_url: (c.youtube_video_url ?? fb.youtube_video_url ?? "").trim(),
        specifications: c.specifications ?? fb.specifications ?? "",
        is_recommended: Boolean(c.is_recommended),
        info_modal_title: c.info_modal_title || fb.info_modal_title || "Deliverables & Scope",
        info_section_heading: c.info_section_heading || fb.info_section_heading || "What we are providing:",
        info_points: parsePoints(c.info_points, fb.info_points),
        prices:
          Array.isArray(c.prices) && c.prices.length > 0
            ? c.prices.map((p) => ({ months: Number(p.months), price: Number(p.price || 0) }))
            : fb.prices || [],
      };
    });
  }
  return fallbackContentTypes;
};

const normalizeManagement = (grp) => {
  const tiers = [];
  const base = grp?.base_tier;
  const adv = grp?.advanced_tier;
  if (base && base.enabled !== false) {
    tiers.push({
      id: "base",
      name: base.name || "Foundation Plan",
      monthly_price: Number(base.monthly_price || 4999),
      description:
        base.description ||
        "Essential brand presence, daily scheduled social publishing & monthly growth analytics.",
      points: (base.features || []).map((f) => f.feature).filter(Boolean),
      period_prices: Array.isArray(base.period_prices)
        ? base.period_prices.map((p) => ({ months: Number(p.months), price: Number(p.price) }))
        : fallbackManagement[0].period_prices,
      is_recommended: false,
    });
  }
  if (adv && adv.enabled !== false) {
    tiers.push({
      id: "advanced",
      name: adv.name || "Growth Plan",
      monthly_price: Number(adv.monthly_price || 8999),
      description:
        adv.description ||
        "Full proactive social growth, viral hook strategy & dedicated account manager.",
      points: (adv.features || []).map((f) => f.feature).filter(Boolean),
      period_prices: Array.isArray(adv.period_prices)
        ? adv.period_prices.map((p) => ({ months: Number(p.months), price: Number(p.price) }))
        : fallbackManagement[1].period_prices,
      is_recommended: true,
    });
  }
  return tiers.length ? tiers : fallbackManagement;
};

const normalizeAds = (grp) => ({
  meta: {
    enabled: grp?.meta_ads?.enabled !== false,
    name: grp?.meta_ads?.name || fallbackAds.meta.name,
    monthly_price: Number(grp?.meta_ads?.monthly_price || fallbackAds.meta.monthly_price),
    description: grp?.meta_ads?.description || fallbackAds.meta.description,
    period_prices: Array.isArray(grp?.meta_ads?.period_prices)
      ? grp.meta_ads.period_prices.map((p) => ({ months: Number(p.months), price: Number(p.price) }))
      : fallbackAds.meta.period_prices,
  },
  google: {
    enabled: grp?.google_ads?.enabled !== false,
    name: grp?.google_ads?.name || fallbackAds.google.name,
    monthly_price: Number(grp?.google_ads?.monthly_price || fallbackAds.google.monthly_price),
    description: grp?.google_ads?.description || fallbackAds.google.description,
    period_prices: Array.isArray(grp?.google_ads?.period_prices)
      ? grp.google_ads.period_prices.map((p) => ({ months: Number(p.months), price: Number(p.price) }))
      : fallbackAds.google.period_prices,
  },
});

const SHOOT_ICONS = {
  camera: FaCamera,
  photoshoot: FaCamera,
  video: FaVideo,
  reel_shoot: FaVideo,
  mic: FaMicrophone,
  podcast_shoot: FaMicrophone,
  drone: TbDrone,
  drone_shoot: TbDrone,
};

const normalizeShoots = (list) => {
  const parseInfoPoints = (raw, fbPoints) => {
    if (Array.isArray(raw) && raw.length > 0) {
      const parsed = raw
        .map((p) =>
          typeof p === "string"
            ? p
            : p?.point || p?.item || p?.text || p?.title || p?.inclusion || ""
        )
        .filter(Boolean);
      if (parsed.length > 0) return parsed;
    }
    return fbPoints || [];
  };
  if (Array.isArray(list) && list.length) {
    return list.map((s) => {
      const id = s.id || s.name?.toLowerCase().replace(/\s+/g, "_");
      const fb = fallbackShoots.find((f) => f.id === id) || {};
      const inclusions =
        Array.isArray(s.inclusions) && s.inclusions.length > 0
          ? s.inclusions.map((i) => (typeof i === "string" ? i : i.item)).filter(Boolean)
          : fb.inclusions || [];
      return {
        id,
        name: s.name || fb.name || "Shoot",
        price: Number(s.price || fb.price || 0),
        unit: s.unit || fb.unit || "per shoot",
        duration: s.duration || fb.duration || "",
        icon: id && SHOOT_ICONS[id] ? id : fb.icon || "camera",
        description: s.description || s.short_description || fb.description || "",
        youtube_video_url: s.youtube_video_url || fb.youtube_video_url || "",
        specifications: s.specifications || fb.specifications || "",
        info_modal_title: s.info_modal_title || fb.info_modal_title || "Deliverables & Scope",
        info_section_heading: s.info_section_heading || fb.info_section_heading || "What we are providing:",
        info_points: parseInfoPoints(s.info_points, inclusions.length > 0 ? inclusions : fb.inclusions || []),
        inclusions,
        note: s.exclusions_note || s.note || fb.note || "",
        period_prices:
          Array.isArray(s.period_prices) && s.period_prices.length > 0
            ? s.period_prices.map((p) => ({ months: Number(p.months), price: Number(p.price || 0) }))
            : fb.period_prices || [],
      };
    });
  }
  return fallbackShoots;
};

const normalizeCoupons = (list) => {
  if (Array.isArray(list) && list.length) {
    return list
      .map((c) => ({
        code: String(c.code || c.coupon_code || "").trim().toUpperCase(),
        discount_percentage: Number(c.discount_percentage || c.discount || 0),
        label: c.label || null,
        is_active: c.is_active !== false && c.enabled !== false,
      }))
      .filter((c) => c.code && c.discount_percentage > 0 && c.is_active);
  }
  return fallbackCoupons;
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */

const StepHeader = ({ num, title, caption }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 border-b border-[#161310]/10 pb-2 sm:pb-3">
    <div className="flex items-baseline gap-2.5 sm:gap-3">
      <span className="dm-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-none tracking-tight text-[#bc1535]/90">
        {num}
      </span>
      <div>
        <h2 className="dm-serif text-lg sm:text-xl font-normal tracking-tight text-[#161310]">
          {title}
        </h2>
        <p className="mt-0.5 text-[11px] sm:text-xs text-[#161310]/55">{caption}</p>
      </div>
    </div>
  </div>
);

const PlanSummaryItem = ({ title, amount, description }) => (
  <details className="dm-plan-item">
    <summary className="dm-plan-item-summary">
      <span className="min-w-0 flex-1 font-semibold text-[#161310]">{title}</span>
      <span className="shrink-0 font-bold text-[#161310]">₹{formatRupees(amount)}</span>
      <LuChevronUp aria-hidden="true" className="dm-plan-item-chevron shrink-0 text-[#bc1535]" />
    </summary>
    <p className="mt-1.5 mb-0 text-[10px] leading-relaxed text-[#161310]/55">{description}</p>
  </details>
);

const DMHomePage = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { addToCart } = useCart();
  const builderRef = useRef(null);
  const pendingCartPlan = useRef(null);
  const cartSubmissionInProgress = useRef(false);

  /* ─── CMS Data ─── */
  const [cmsData, setCmsData] = useState(null);
  const [cmsMediaOrigin, setCmsMediaOrigin] = useState("https://admin.indorinama.in");
  const [loadingCms, setLoadingCms] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchCmsData = async () => {
      // Cache-buster (_t) bypasses the axios in-memory GET cache so admin
      // edits to (i) popup content, prices and coupons reflect immediately.
      const bust = { _t: Date.now() };
      // NOTE: https://admin.indorinama.in is the live backend and must stay
      // in this list — production builds have no REACT_APP_PAYLOAD_URL and
      // localhost/relative URLs fail in real browsers, which previously left
      // the page stuck on stale fallback prices (e.g. the old ₹4,999 Meta fee).
      const endpoints = [
        process.env.REACT_APP_PAYLOAD_URL
          ? `${process.env.REACT_APP_PAYLOAD_URL}/api/globals/digital-marketing`
          : null,
        "http://localhost:8000/api/globals/digital-marketing",
        "https://admin.indorinama.in/api/globals/digital-marketing",
        "/api/globals/digital-marketing",
      ].filter(Boolean);

      for (const endpoint of endpoints) {
        try {
          const res = await axios.get(endpoint, { timeout: 4000, params: bust });
          if (res.data && (res.data.content_types || res.data.other_services || res.data.shoots_rate_card || res.data.globalType === "digital-marketing")) {
            if (mounted) {
              setCmsData(res.data);
              setCmsMediaOrigin(new URL(endpoint, res.config?.baseURL || window.location.origin).origin);
              setLoadingCms(false);
            }
            return;
          }
        } catch (err) {
          // try next endpoint
        }
      }
      if (mounted) setLoadingCms(false);
    };

    fetchCmsData();
    return () => {
      mounted = false;
    };
  }, []);

  const durations = useMemo(() => normalizeDurations(cmsData?.durations), [cmsData]);
  const contentTypes = useMemo(() => normalizeContentTypes(cmsData?.content_types), [cmsData]);
  const managementOptions = useMemo(
    () => normalizeManagement(cmsData?.account_management),
    [cmsData]
  );
  const adsConfig = useMemo(() => normalizeAds(cmsData?.ad_campaigns), [cmsData]);
  const shootServices = useMemo(() => normalizeShoots(cmsData?.shoots_rate_card), [cmsData]);
  const otherServices = useMemo(() => {
    const liveList =
      cmsData?.other_services && Array.isArray(cmsData.other_services) && cmsData.other_services.length > 0
        ? cmsData.other_services
        : [];
    // Merge admin/live data over fallbacks (by slug or aliases), then
    // enforce the requested display order. New admin slugs not in
    // fallbacks are appended in admin order so nothing gets hidden.
    const mergedBySlug = new Map();
    fallbackOtherServices.forEach((fb) => {
      const live = liveList.find(
        (s) => s.slug === fb.slug || (fb.aliases && fb.aliases.includes(s.slug))
      );
      mergedBySlug.set(fb.slug, live ? { ...fb, ...live } : fb);
    });
    liveList.forEach((s) => {
      if (!s?.slug) return;
      const known = fallbackOtherServices.find(
        (fb) => fb.slug === s.slug || (fb.aliases && fb.aliases.includes(s.slug))
      );
      if (!known) mergedBySlug.set(s.slug, s);
    });
    const ordered = [];
    OTHER_SERVICES_ORDER.forEach((slug) => {
      if (mergedBySlug.has(slug)) {
        ordered.push(mergedBySlug.get(slug));
        mergedBySlug.delete(slug);
      } else {
        // alias fallback (e.g. email vs mail)
        for (const [key, val] of Array.from(mergedBySlug.entries())) {
          if (val?.slug === slug || (val?.aliases && val.aliases.includes(slug))) {
            ordered.push(val);
            mergedBySlug.delete(key);
            break;
          }
        }
      }
    });
    return ordered.length > 0 ? ordered : fallbackOtherServices.filter((fb) => OTHER_SERVICES_ORDER.includes(fb.slug));
  }, [cmsData]);
  const coupons = useMemo(() => normalizeCoupons(cmsData?.coupons), [cmsData]);

  const heroStats = useMemo(() => {
    if (Array.isArray(cmsData?.hero_stats) && cmsData.hero_stats.length > 0) {
      return cmsData.hero_stats;
    }
    return fallbackHeroStats;
  }, [cmsData]);

  /* ─── Duration Slider Index State ─── */
  const [sliderIndex, setSliderIndex] = useState(1);

  // Sync default duration once loaded
  useEffect(() => {
    if (durations.length) {
      const defaultIdx = durations.findIndex((d) => d.is_default);
      setSliderIndex(defaultIdx >= 0 ? defaultIdx : Math.min(1, durations.length - 1));
    }
  }, [durations]);

  const activeDuration = durations[sliderIndex] || durations[0] || fallbackDurations[0];
  const months = activeDuration.months;
  const durationLabel = activeDuration.label;
  // Long label requested: "3 month Total" instead of "3 Mo Total"
  const durationLongLabel = `${months} month`;
  const discountPct = activeDuration.discount_percentage || 0;
  const discountDecimal = discountPct / 100;

  /* ─── Configurator Selections ─── */
  const [quantities, setQuantities] = useState({});
  const [selectedManagementId, setSelectedManagementId] = useState(null);
  const [enableMetaAds, setEnableMetaAds] = useState(false);
  const [enableGoogleAds, setEnableGoogleAds] = useState(false);
  const [dailyBudget, setDailyBudget] = useState(1000);
  const [spendMode, setSpendMode] = useState("day"); // "day" | "month"
  const [selectedShoots, setSelectedShoots] = useState({});
  // Coupon state (codes configured from Digital Marketing global in admin)
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponPct, setCouponPct] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  // Active info modal for "i" button on content types + shoots
  const [activeInfoModal, setActiveInfoModal] = useState(null);
  const [activeShootModal, setActiveShootModal] = useState(null);

  // Form data for proposal & cart
  const [formData, setFormData] = useState({
    company: "",
    companyCategory: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  // UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [regModal, setRegModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  // Sync user info
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || user.name || "",
        email: prev.email || user.email || "",
        phone: prev.phone || user.phone || "",
        company: prev.company || "",
      }));
    }
  }, [user]);

  // Scroll listener for sticky summary on mobile
  useEffect(() => {
    const handleScroll = () => {
      if (builderRef.current) {
        const rect = builderRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 60);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ─── Pricing Engine ─── */

  const getUnitPrice = (typeId) => {
    const t = contentTypes.find((c) => c.id === typeId);
    if (!t) return 0;
    const match = (t.prices || []).find((p) => Number(p.months) === Number(months));
    if (match && Number(match.price) > 0) return Number(match.price);
    const base = Number((t.prices || [])[0]?.price || 0);
    return Math.round(base * (1 - discountDecimal));
  };

  const getBaseUnitPrice = (typeId) => {
    const t = contentTypes.find((c) => c.id === typeId);
    if (!t) return 0;
    const match = (t.prices || []).find((p) => Number(p.months) === 1);
    if (match && Number(match.price) > 0) return Number(match.price);
    return Number((t.prices || [])[0]?.price || 0);
  };

  const getShootPrice = (shoot) => {
    if (!shoot) return 0;
    if (Array.isArray(shoot.period_prices) && shoot.period_prices.length > 0) {
      const match = shoot.period_prices.find((p) => Number(p.months) === Number(months));
      if (match && Number(match.price) > 0) return Number(match.price);
    }
    const base = Number(shoot.price || 0);
    return Math.round(base * (1 - discountDecimal));
  };

  const getBaseShootPrice = (shoot) => {
    if (!shoot) return 0;
    if (Array.isArray(shoot.period_prices) && shoot.period_prices.length > 0) {
      const match = shoot.period_prices.find((p) => Number(p.months) === 1);
      if (match && Number(match.price) > 0) return Number(match.price);
    }
    return Number(shoot.price || 0);
  };

  const activeCreativeTypes = useMemo(
    () => contentTypes.filter((t) => (quantities[t.id] || 0) > 0),
    [contentTypes, quantities]
  );

  // Content unit prices are per-unit per-month rates (e.g. ₹110/post when
  // on a 3-month plan), so the full-period total is qty × unitPrice × months.
  const creativeTotal = useMemo(
    () =>
      contentTypes.reduce(
        (sum, t) => sum + (quantities[t.id] || 0) * getUnitPrice(t.id),
        0
      ) * months,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentTypes, quantities, months, discountDecimal]
  );

  const creativeBaseTotal = useMemo(
    () =>
      contentTypes.reduce(
        (sum, t) => sum + (quantities[t.id] || 0) * getBaseUnitPrice(t.id),
        0
      ) * months,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentTypes, quantities, months]
  );

  const creativeSavings = Math.max(0, creativeBaseTotal - creativeTotal);

  const selectedManagement = useMemo(
    () => managementOptions.find((m) => m.id === selectedManagementId) || null,
    [managementOptions, selectedManagementId]
  );

  // Frontend ads budget (Per Day / Per Month input below). Displayed for
  // reference only — paid directly to Meta/Google, never billed.
  const monthlyBudget = dailyBudget * 30;
  const periodBudget = monthlyBudget * months;
  const displayBudget = spendMode === "month" ? monthlyBudget : dailyBudget;
  const handleBudgetInput = (val) => {
    const parsed = Number.parseInt(String(val), 10);
    if (!Number.isFinite(parsed)) {
      setDailyBudget(0);
      return;
    }
    if (spendMode === "month") {
      setDailyBudget(Math.max(0, Math.round(parsed / 30)));
    } else {
      setDailyBudget(Math.max(0, parsed));
    }
  };

  const metaAds = adsConfig.meta;
  const googleAds = adsConfig.google;
  const metaOn = enableMetaAds && metaAds.enabled;
  const googleOn = enableGoogleAds && googleAds.enabled;

  // Retainer total for management/ads. period_prices entries are normally the
  // FULL period total, but the admin sheet has historically held per-month
  // rates there instead (e.g. 2300 for 3 months with monthly 2500).
  // Disambiguate: genuine totals are >= ~50% of monthly × months; smaller
  // values are per-month rates and get multiplied by months.
  const calculatePeriodPrice = (item, durationMonths, discountRate) => {
    if (!item) return 0;
    const monthsNum = Number(durationMonths) || 0;
    const monthly = Number(item.monthly_price || 0);
    if (Array.isArray(item.period_prices) && item.period_prices.length > 0) {
      const match = item.period_prices.find((p) => Number(p.months) === monthsNum);
      const override = Number(match?.price || 0);
      if (override > 0) {
        if (monthly > 0 && override < monthly * monthsNum * 0.5) {
          return Math.round(override * monthsNum);
        }
        return override;
      }
    }
    return Math.round(monthly * monthsNum * (1 - discountRate));
  };

  const mgmtMonthly = selectedManagement ? selectedManagement.monthly_price : 0;

  const mgmtTotal = selectedManagement
    ? calculatePeriodPrice(selectedManagement, months, discountDecimal)
    : 0;

  // User-selected ad spend budget from the slider/input is included in overall cost
  const adsLabel = metaOn && googleOn ? "Meta + Google" : metaOn ? "Meta" : "Google";
  const adsTotal = (metaOn || googleOn) ? periodBudget : 0;

  const mgmtSavings = selectedManagement
    ? Math.max(0, Math.round(mgmtMonthly * months) - mgmtTotal)
    : 0;
  const servicesRecurring = mgmtTotal + adsTotal;
  const servicesSavings = mgmtSavings;

  const shootsTotal = useMemo(() => {
    let sum = 0;
    Object.entries(selectedShoots).forEach(([id, qty]) => {
      const shoot = shootServices.find((s) => s.id === id);
      if (shoot && qty > 0) {
        sum += getShootPrice(shoot) * qty;
      }
    });
    return sum;
  }, [selectedShoots, shootServices, months, discountDecimal]);

  const shootsBaseTotal = useMemo(() => {
    let sum = 0;
    Object.entries(selectedShoots).forEach(([id, qty]) => {
      const shoot = shootServices.find((s) => s.id === id);
      if (shoot && qty > 0) {
        sum += getBaseShootPrice(shoot) * qty;
      }
    });
    return sum;
  }, [selectedShoots, shootServices, months]);

  const shootsSavings = Math.max(0, shootsBaseTotal - shootsTotal);
  const totalSavings = servicesSavings + shootsSavings + creativeSavings;

  // Unit math note: content prices are ₹ per unit per month, so creativeTotal
  // is already qty × unitPrice × months. Shoot prices are per-occurrence (not
  // monthly), so shootsTotal is qty × shootPrice with no month multiplier.
  // Management period_prices are full-period totals; ads are paid directly.
  const subtotal = creativeTotal + servicesRecurring + shootsTotal;
  // Coupon discount applies on the duration-discounted subtotal (pre-GST)
  const couponDiscount = couponApplied && couponPct > 0 ? Math.round(subtotal * (couponPct / 100)) : 0;
  const totalDiscount = totalSavings + couponDiscount;
  const taxableValue = Math.max(0, subtotal - couponDiscount);
  const gstAmount = Math.round(taxableValue * 0.18);
  const grandTotal = taxableValue + gstAmount;
  const monthlyEquivalent = Math.round(grandTotal / months);

  /* ─── Handlers ─── */
  const updateQty = (id, val) => {
    const parsed = Number.parseInt(String(val), 10);
    const n = Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 999) : 0;
    setQuantities((prev) => ({ ...prev, [id]: n }));
  };

  const updateShootQty = (id, delta) => {
    setSelectedShoots((prev) => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      }
      return { ...prev, [id]: next };
    });
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    setCouponError("");
    if (!code) {
      setCouponError("Please enter a coupon code");
      return;
    }
    // 1) Local check against admin-configured coupons (Digital Marketing global)
    const local = (coupons || []).find((c) => c.code === code);
    if (local) {
      setCouponApplied(true);
      setCouponPct(Number(local.discount_percentage || 0));
      setCouponCode(code);
      toast.success(`Coupon ${code} applied — ${local.discount_percentage}% off`);
      return;
    }
    // 2) Backend validation fallback (supports INDORI18 + collection coupons)
    setCouponLoading(true);
    try {
      const res = await axios.post("/api/validate-coupon", {
        code,
        collection: "digital-marketing",
        productId: "digital-marketing",
      });
      if (res.data?.valid) {
        setCouponApplied(true);
        setCouponPct(Number(res.data.discountPercentage || 0));
        setCouponCode(code);
        toast.success(`Coupon ${code} applied`);
      } else {
        setCouponError("Invalid coupon code");
        setCouponApplied(false);
        setCouponPct(0);
      }
    } catch (err) {
      setCouponError(err?.response?.data?.error || "Invalid coupon code");
      setCouponApplied(false);
      setCouponPct(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponPct(0);
    setCouponCode("");
    setCouponError("");
  };

  const planItems = [
    ...activeCreativeTypes.map((t) => {
      const qty = quantities[t.id] || 0;
      const price = getUnitPrice(t.id);
      return {
        id: `creative-${t.id}`,
        title: `${qty} × ${t.name}`,
        amount: qty * price * months,
        description: `₹${formatRupees(price)}/${t.unit}/month × ${qty} × ${months} months`,
      };
    }),
    ...(selectedManagement ? [{
      id: "management",
      title: selectedManagement.name,
      amount: mgmtTotal,
      description: `${months} months${discountPct > 0 ? ` · ${discountPct}% off` : ""} · Average ₹${formatRupees(Math.round(mgmtTotal / months))}/month`,
    }] : []),
    ...((metaOn || googleOn) ? [{
      id: "ad-spend",
      title: `${adsLabel} Ad Spend`,
      amount: adsTotal,
      description: `₹${formatRupees(dailyBudget)}/day (~₹${formatRupees(monthlyBudget)}/month) × ${months} months`,
    }] : []),
    ...Object.entries(selectedShoots).flatMap(([id, qty]) => {
      const shoot = shootServices.find((service) => service.id === id);
      if (!shoot || qty <= 0) return [];
      const price = getShootPrice(shoot);
      return [{
        id: `shoot-${id}`,
        title: `${qty} × ${shoot.name}`,
        amount: price * qty,
        description: `₹${formatRupees(price)}/shoot × ${qty} · ${durationLabel} plan`,
      }];
    }),
  ];

  // Keep the displayed scope and both enquiry channels in sync with the builder.
  const planDetails = [
    `Duration: ${durationLabel} (${months} months, ${discountPct}% duration discount)`,
    formData.company ? `Company / Brand: ${formData.company}` : null,
    formData.companyCategory ? `Industry: ${formData.companyCategory}` : null,
    ...planItems.map((item) => `${item.title}: ₹${formatRupees(item.amount)} (${item.description})`),
    activeCreativeTypes.length === 0 ? "Content & creatives: None selected" : null,
    selectedManagement
      ? `Management monthly base: ₹${formatRupees(mgmtMonthly)} × ${months} months`
      : "Management: None selected",
    `Meta Ads: ${metaOn ? "Selected" : "Not selected"}`,
    `Google Ads: ${googleOn ? "Selected" : "Not selected"}`,
    metaOn || googleOn
      ? `Ad budget input: Per ${spendMode === "month" ? "month" : "day"}, ₹${formatRupees(displayBudget)}; planned spend ₹${formatRupees(dailyBudget)}/day, ₹${formatRupees(monthlyBudget)}/month, ₹${formatRupees(periodBudget)} for the full plan. Paid directly to Meta/Google; included in this estimate.`
      : null,
    !planItems.some((item) => item.id.startsWith("shoot-")) ? "Shoots & services: None selected" : null,
    `Content & creatives total: ₹${formatRupees(creativeTotal)}`,
    `Management total: ₹${formatRupees(mgmtTotal)}`,
    `Ad spend total: ₹${formatRupees(adsTotal)}`,
    `Shoots & services total: ₹${formatRupees(shootsTotal)}`,
    `Price before discounts: ₹${formatRupees(subtotal + totalSavings)}`,
    `Duration savings: ₹${formatRupees(totalSavings)}`,
    `Subtotal after duration savings: ₹${formatRupees(subtotal)}`,
    couponApplied
      ? `Coupon: ${couponCode} (${couponPct}% off, savings ₹${formatRupees(couponDiscount)})`
      : "Coupon: None applied",
    `Total discount: ₹${formatRupees(totalDiscount)}`,
    `Taxable value: ₹${formatRupees(taxableValue)}`,
    `GST (18%): ₹${formatRupees(gstAmount)}`,
    `Total investment (incl. GST): ₹${formatRupees(grandTotal)}`,
    `Average monthly investment (incl. GST): ₹${formatRupees(monthlyEquivalent)}`,
    formData.notes ? `Goals / Notes: ${formData.notes}` : null,
  ].filter(Boolean);

  const waMessage = [
    "Hi Indorinama team, I am interested in this customized digital marketing plan.",
    ...planDetails,
    formData.name ? `Name: ${formData.name}` : null,
    formData.phone ? `Phone / WhatsApp: ${formData.phone}` : null,
    formData.email ? `Email: ${formData.email}` : null,
  ].filter(Boolean).join("\n");
  const waLink = `https://wa.me/918989801088?text=${encodeURIComponent(waMessage)}`;

  const scrollToBuilder = () =>
    document.getElementById("plan-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const savePlanToCart = useCallback(async (cartData) => {
    if (cartSubmissionInProgress.current) return;
    cartSubmissionInProgress.current = true;
    setIsAddingToCart(true);
    try {
      const savedItem = await addToCart(`Digital Marketing (${cartData.quantity.duration_label})`, "digital-marketing", cartData);
      if (!savedItem?.id && !savedItem?._id) {
        throw new Error("The plan could not be saved. Please sign in and try again.");
      }
      toast.success("Digital Marketing plan added to cart!");
      setIsDrawerOpen(false);
      navigate("/cart");
    } catch (err) {
      console.error("Failed to add digital marketing to cart:", err);
      toast.error(err?.response?.data?.errors?.[0]?.message || err?.response?.data?.message || err?.message || "Failed to add plan to cart");
    } finally {
      cartSubmissionInProgress.current = false;
      setIsAddingToCart(false);
    }
  }, [addToCart, navigate]);

  // Consume the selected plan once authentication completes, using the new session.
  useEffect(() => {
    if (!isLoggedIn || !user || !pendingCartPlan.current) return;
    const plan = pendingCartPlan.current;
    pendingCartPlan.current = null;
    setRegModal(false);
    savePlanToCart(plan);
  }, [isLoggedIn, user, savePlanToCart]);

  const handleAddToCart = async () => {
    if (cartSubmissionInProgress.current) return;
    if (grandTotal <= 0) {
      toast.error("Please select at least one creative or service for your plan.");
      return;
    }

    const creativeSummary = activeCreativeTypes
      .map((t) => `${quantities[t.id]}x ${t.name}`)
      .join(", ");

    const shootsSummary = Object.entries(selectedShoots)
      .map(([id, q]) => {
        const shoot = shootServices.find((s) => s.id === id);
        return shoot && q > 0 ? `${q}x ${shoot.name} @ ₹${formatRupees(getShootPrice(shoot))}` : null;
      })
      .filter(Boolean)
      .join(", ");

    const descriptionParts = [
      `Duration: ${durationLabel} (${months} months${discountPct > 0 ? `, ${discountPct}% off` : ""})`,
      formData.company ? `Brand: ${formData.company}` : null,
      formData.companyCategory ? `Industry: ${formData.companyCategory}` : null,
      creativeSummary ? `Creatives: ${creativeSummary}` : null,
      selectedManagement ? `Management: ${selectedManagement.name} (₹${formatRupees(mgmtTotal)})` : null,
      metaOn || googleOn ? `Paid Ads (${adsLabel}): ₹${formatRupees(dailyBudget)}/day (~₹${formatRupees(monthlyBudget)}/mo, ₹${formatRupees(periodBudget)} for ${durationLongLabel})` : null,
      shootsSummary ? `Shoots: ${shootsSummary}` : null,
      couponApplied ? `Coupon: ${couponCode} (${couponPct}% off)` : null,
      formData.notes ? `Goal/Notes: ${formData.notes}` : null,
    ].filter(Boolean);

    const cartData = {
      name: `Digital Marketing Package (${durationLabel})`,
      description: descriptionParts.join(" • "),
      image_url: "/dm-digital-marketing.svg",
      quantity: {
        duration_months: months,
        duration_label: durationLabel,
        brand_name: formData.company || "",
        industry: formData.companyCategory || "",
        campaign_notes: formData.notes || "",
        creatives: quantities,
        management: selectedManagement?.name || "None",
        meta_ads: metaOn,
        google_ads: googleOn,
        ads_billing: spendMode,
        daily_ad_spend: dailyBudget,
        monthly_ad_spend: monthlyBudget,
        shoots: selectedShoots,
        ...(couponApplied ? { coupon_code: couponCode } : {}),
      },
      subtotal: subtotal + totalSavings,
      discount_amount: totalDiscount,
      tax_amount: gstAmount,
      total_amount: grandTotal,
    };

    if (!isLoggedIn || !user) {
      pendingCartPlan.current = cartData;
      setRegModal(true);
      return;
    }
    await savePlanToCart(cartData);
  };

  const handleSubmitProposal = async (e) => {
    e?.preventDefault();
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please provide your name, phone number, and email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the shared axios baseURL (REACT_APP_BACKEND_URL) via relative path.
      // Falls back to PAYLOAD URL explicitly when baseURL is not configured.
      const explicitBase = (process.env.REACT_APP_PAYLOAD_URL || "").replace(/\/$/, "");
      const endpoint = explicitBase
        ? `${explicitBase}/api/send-digital-marketing-email`
        : "/api/send-digital-marketing-email";

      await axios.post(endpoint, {
        planName: `Custom Digital Marketing (${durationLabel})`,
        planFeatures: planDetails,
        price: grandTotal,
        customerName: formData.name,
        phoneNumber: formData.phone,
        companyName: formData.company || "Individual / Business",
        email: formData.email,
      });

      toast.success("Thank you! Your customized proposal request has been received. Our team will contact you shortly.");
      setIsModalOpen(false);
      setIsDrawerOpen(false);
    } catch (err) {
      console.error("Error submitting digital marketing request:", err);
      toast.error(err?.response?.data?.error || "Failed to submit proposal. Please try again or chat on WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Shared Visuals ─── */

  const CheckDot = ({ active }) => (
    <span
      className={`flex h-4 w-4 sm:h-[18px] sm:w-[18px] shrink-0 items-center justify-center rounded-full border text-[8px] sm:text-[9px] transition-colors ${active
          ? "border-[#bc1535] bg-[#bc1535] text-white"
          : "border-[#161310]/25 bg-white text-transparent"
        }`}
    >
      <FaCheck />
    </span>
  );

  const reveal = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.5, ease: easeOut },
  };

  const qtyStepper = (t) => {
    const qty = quantities[t.id] || 0;
    return (
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[#161310]/40 font-semibold">
          Qty
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={`Decrease ${t.name}`}
            onClick={() => updateQty(t.id, qty - 1)}
            disabled={qty <= 0}
            className="flex h-6 w-6 items-center justify-center rounded-md bg-[#161310] text-[#f5efe8] transition hover:bg-[#3a332c] active:scale-95 disabled:opacity-20"
          >
            <FaMinus className="text-[7px]" />
          </button>
          <input
            type="number"
            min={0}
            max={999}
            value={qty || ""}
            onChange={(e) => updateQty(t.id, e.target.value)}
            placeholder="0"
            aria-label={`${t.name} quantity`}
            className="no-spinner h-6 w-9 rounded-md border border-[#161310]/15 bg-white text-center text-xs font-bold text-[#161310] outline-none transition focus:border-[#bc1535]"
          />
          <button
            type="button"
            aria-label={`Increase ${t.name}`}
            onClick={() => updateQty(t.id, qty + 1)}
            className="flex h-6 w-6 items-center justify-center rounded-md bg-[#161310] text-[#f5efe8] transition hover:bg-[#3a332c] active:scale-95"
          >
            <FaPlus className="text-[7px]" />
          </button>
        </div>
      </div>
    );
  };

  // Slider background track percentage
  const maxIdx = Math.max(1, durations.length - 1);
  const sliderPercentage = (sliderIndex / maxIdx) * 100;

  return (
    <div className="dm-paper min-h-screen text-[#161310]">
      <SEO
        title="Digital Marketing & Content Production | Indorinama"
        description="Build your customized social media marketing plan, ad campaigns, and on-ground shoots with Indorinama. Transparent pricing and maximum ROI."
        keywords="digital marketing indore, social media marketing, meta ads, photoshoot, reel shoot, podcast shoot indore"
        url="https://indorinama.com/digital-marketing"
      />

      <Navbar />
      <Carousel />

      {/* ═══ HERO — editorial band ═══ */}
      <section className="dm-hero-bg relative overflow-hidden text-[#f5efe8]">
        <div className="relative mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Column: Headline, Copy & CTAs */}
            <motion.div
              variants={heroContainer}
              initial="hidden"
              animate="show"
              className="lg:col-span-8 max-w-3xl"
            >
              <motion.h1
                variants={heroItem}
                className="dm-serif text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl"
              >
                Grow where your{" "}
                <em className="font-normal italic text-[#ff8ca0]">customers</em>{" "}
                scroll.
              </motion.h1>

              <motion.p
                variants={heroItem}
                className="mt-3 max-w-xl text-xs sm:text-sm leading-relaxed text-[#f5efe8]/70"
              >
                Indorinama runs your entire digital engine — creative content,
                account growth, Meta &amp; Google campaigns and on-ground shoots —
                in one transparent plan. Build it in minutes, launch in days.
              </motion.p>

              <motion.div variants={heroItem} className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={scrollToBuilder}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#bc1535] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-[0_14px_32px_-12px_rgba(188,21,53,0.85)] transition hover:bg-[#a10f2b] active:scale-[0.98]"
                >
                  Build your plan
                  <LuArrowDown className="text-xs transition-transform group-hover:translate-y-0.5" />
                </button>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#f5efe8]/25 bg-transparent px-4 py-2.5 text-xs sm:text-sm font-semibold text-[#f5efe8]/90 transition hover:border-[#f5efe8]/60 hover:bg-[#f5efe8]/5"
                >
                  <FaWhatsapp className="text-emerald-400 text-sm" />
                  Chat on WhatsApp
                </a>
              </motion.div>
            </motion.div>

            {/* Right Column: Full Indorinama Logo Card Presentation */}
          </div>

          {/* Editorial stats row: Customers, Campaigns, Years, Certifications */}
          <motion.div
            variants={heroContainer}
            initial="hidden"
            animate="show"
            className="mt-4 pt-4 sm:mt-10 sm:pt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-[#f5efe8]/15 lg:grid-cols-4"
          >
            {heroStats.map((stat, idx) => (
              <motion.div
                key={idx}
                variants={heroItem}
                className="lg:border-l lg:border-[#f5efe8]/15 lg:pl-5 lg:first:border-l-0 lg:first:pl-0"
              >
                <div className="dm-serif text-2xl font-light text-[#f5efe8] sm:text-3xl">
                  {stat.value}
                </div>
                <div className="dm-serif mt-1 text-sm italic leading-snug text-[#ff8ca0] sm:text-[15px]">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Videos category="digital-marketing" embedded
        title={cmsData?.top_videos_title || "Digital marketing in action"}
        configuredVideos={cmsData?.top_videos_use_legacy === true ? undefined : cmsData?.top_videos_use_legacy === false || cmsData?.top_videos?.length ? (cmsData?.top_videos || []) : undefined}
      />

      {/* ═══ FIXED / STICKY TOP PRICE & DURATION BAR (Mobile & Tablet Only) ═══ */}
      <AnimatePresence>
        {isSticky && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="dm-sticky-top-bar sticky top-[80px] md:top-[128px] z-40 w-full border-b border-[#161310]/15 lg:hidden shadow-md"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-2 px-3 py-2 sm:px-6">
              {/* Row 1: Plan Builder total & CTAs */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                  <span className="dm-serif text-xl sm:text-2xl font-bold tracking-tight text-[#bc1535]">
                    ₹{formatRupees(grandTotal)}
                  </span>
                  <span className="text-[10px] text-[#161310]/50 font-medium">
                    (incl. GST)
                  </span>
                  {months > 1 && (
                    <span className="text-[10px] font-semibold text-[#161310]/70">Avg. ₹{formatRupees(monthlyEquivalent)}/month</span>
                  )}
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsDrawerOpen(true)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#161310]/15 bg-white px-2.5 py-1 text-[11px] font-bold text-[#161310] shadow-sm hover:border-[#161310]/30"
                  >
                    <span>Details</span>
                    <LuChevronUp className="text-xs" />
                  </button>

                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                    className="inline-flex items-center gap-1 rounded-full bg-[#bc1535] px-3 py-1 text-[11px] font-bold text-white shadow-sm hover:bg-[#a10f2b] active:scale-95 disabled:opacity-75"
                  >
                    <FaCartShopping className="text-[10px]" />
                    <span>{isAddingToCart ? "..." : "Cart"}</span>
                  </button>
                </div>
              </div>

              {/* Row 2: Compact duration pills */}
              <div className="flex items-center justify-between gap-1 border-t border-[#161310]/10 pt-1.5">
                <div className="flex items-center gap-1 overflow-x-auto py-0.5 no-scrollbar">
                  {durations.map((d, idx) => (
                    <button
                      key={d.months}
                      type="button"
                      onClick={() => setSliderIndex(idx)}
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-all ${sliderIndex === idx
                          ? "bg-[#bc1535] text-white shadow-sm"
                          : "bg-white text-[#161310]/60 border border-[#161310]/10 hover:border-[#161310]/25"
                        }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                {discountPct > 0 && (
                  <span className="shrink-0 text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full shadow-sm">
                    {discountPct}% off
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ PLAN BUILDER ANCHOR (header text removed per request) ═══ */}
      <div id="plan-builder" ref={builderRef} className="scroll-mt-36" />

      {/* ═══ MAIN: CONFIGURATOR + STICKY SUMMARY ═══ */}
      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
          {/* ─── LEFT: MODULES ─── */}
          <div className="space-y-5 sm:space-y-6 lg:col-span-8">
            {/* 01 · Brand & Campaign Brief (Optional) — first on top */}
            <motion.section {...reveal}>
              <StepHeader
                num="01"
                title="Brand & Campaign Brief (Optional)"
                caption="Share your business name and campaign goals so our strategy team can personalize your creatives."
              />

              <div className="mt-3 rounded-2xl border border-[#161310] bg-[#161310] p-3 sm:p-4 space-y-3 shadow-md">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-white/90">
                      Brand / Business Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Chai Sutta Bar"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full rounded-xl border border-[#161310]/20 bg-white px-3.5 py-2 text-xs sm:text-sm font-medium text-black outline-none transition placeholder:text-black/50 focus:border-[#bc1535] focus:ring-2 focus:ring-[#bc1535]/20"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-white/90">
                      Industry / Category
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. F&B / Cafe / Real Estate"
                      value={formData.companyCategory}
                      onChange={(e) =>
                        setFormData({ ...formData, companyCategory: e.target.value })
                      }
                      className="w-full rounded-xl border border-[#161310]/20 bg-white px-3.5 py-2 text-xs sm:text-sm font-medium text-black outline-none transition placeholder:text-black/50 focus:border-[#bc1535] focus:ring-2 focus:ring-[#bc1535]/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-white/90">
                    Specific Campaign Goal or Target Audience
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Need high-converting Instagram reels & leads for our new store opening..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full rounded-xl border border-[#161310]/20 bg-white px-3.5 py-2 text-xs sm:text-sm font-medium text-black outline-none transition placeholder:text-black/50 focus:border-[#bc1535] focus:ring-2 focus:ring-[#bc1535]/20"
                  />
                </div>
              </div>
            </motion.section>

            {/* 02 · Social Content Creatives */}
            <motion.section {...reveal}>
              <StepHeader
                num="02"
                title="Social Content Creatives"
                caption="Select quantities for each content format. Tap the (i) button to see what we are providing."
              />

              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-3 xl:grid-cols-3">
                {contentTypes.map((t) => {
                  const qty = quantities[t.id] || 0;
                  const price = getUnitPrice(t.id);
                  return (
                    <div
                      key={t.id}
                      className={`dm-option-card relative flex flex-col justify-between rounded-xl border bg-[#fcfaf7] p-2.5 sm:p-3 ${qty > 0
                          ? "border-[#bc1535] bg-[#fff6f4] ring-1 ring-[#bc1535]/25"
                          : "border-[#161310]/10 hover:border-[#161310]/30 hover:shadow-md"
                        }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-1.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#bc1535]/10 text-base text-[#bc1535]">
                            <ContentTypeIcon typeKey={t.id || t.icon} className="text-base text-[#bc1535]" />
                          </span>

                          <div className="flex items-center gap-1.5">
                            {t.is_recommended && (
                              <span className="rounded-full bg-[#bc1535] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white">
                                Popular
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveInfoModal(t);
                              }}
                              title="What we are providing"
                              aria-label={`What we provide for ${t.name}`}
                              className="flex h-5 w-5 items-center justify-center rounded-full border border-[#161310]/20 bg-white text-[10px] font-bold text-[#161310]/70 hover:border-[#bc1535] hover:bg-[#bc1535] hover:text-white transition shadow-sm"
                            >
                              i
                            </button>
                          </div>
                        </div>

                        <h3 className="mt-2 text-xs sm:text-[13px] font-bold leading-snug text-[#161310]">
                          {t.name}
                        </h3>
                        <p className="mt-0.5 text-[10px] leading-relaxed text-[#161310]/55 line-clamp-2">
                          {t.description}
                        </p>
                      </div>

                      <div className="mt-2.5 border-t border-[#161310]/10 pt-2">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          {discountPct > 0 && (
                            <span className="text-[11px] text-[#161310]/35 line-through">
                              ₹{formatRupees(getBaseUnitPrice(t.id))}
                            </span>
                          )}
                          <span className="dm-serif text-lg font-bold leading-none text-[#bc1535]">
                            ₹{formatRupees(price)}
                          </span>
                          <span className="text-[9px] text-[#161310]/45">
                            /{t.unit}
                          </span>
                        </div>
                        {qtyStepper(t)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>

            {/* 03 · Account Management */}
            <motion.section {...reveal}>
              <StepHeader
                num="03"
                title="Account Management & Growth"
                caption="Select one tier for social publishing, community engagement, and monthly growth strategy."
              />

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {managementOptions.map((opt) => {
                  const isSelected = selectedManagementId === opt.id;
                  const periodTotal = calculatePeriodPrice(opt, months, discountDecimal);
                  const baseTotal = Number(opt.monthly_price || 0) * months;
                  const savedAmt = Math.max(0, Math.round(baseTotal - periodTotal));
                  return (
                    <div
                      key={opt.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedManagementId((prev) => (prev === opt.id ? null : opt.id))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedManagementId((prev) => (prev === opt.id ? null : opt.id));
                        }
                      }}
                      className={`dm-option-card dm-card-focus flex cursor-pointer flex-col justify-between rounded-2xl border p-3 sm:p-4 ${isSelected
                          ? "border-[#bc1535] bg-[#fff6f4] ring-1 ring-[#bc1535]/25 shadow-sm"
                          : "border-[#161310]/10 bg-[#fcfaf7] hover:border-[#161310]/30 hover:shadow-md"
                        }`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckDot active={isSelected} />
                          <span className="text-sm sm:text-base font-bold text-[#161310]">
                            {opt.name}
                          </span>
                          {opt.is_recommended && (
                            <span className="rounded-full bg-[#bc1535] px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white">
                              Popular
                            </span>
                          )}
                        </div>

                        <p className="mt-1.5 text-xs text-[#161310]/60">
                          {opt.description}
                        </p>

                        <ul className="mt-2.5 space-y-1">
                          {opt.points.map((pt) => (
                            <li
                              key={pt}
                              className="flex items-start gap-1.5 text-[11px] text-[#161310]/70"
                            >
                              <FaCheck className="mt-0.5 shrink-0 text-[8px] text-[#bc1535]" />
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-3 flex items-start justify-between border-t border-[#161310]/10 pt-2">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#bc1535]">
                          {months > 1 ? `${months} month Total` : "Per Month"}
                          {months > 1 && savedAmt > 0 && (
                            <span className="mt-0.5 block text-[10px] font-bold normal-case tracking-normal text-emerald-600">
                              (₹{formatRupees(savedAmt)} you saved)
                            </span>
                          )}
                        </span>
                        <div className="text-right">
                          {discountPct > 0 && (
                            <span className="mr-1.5 text-xs text-[#161310]/35 line-through">
                              ₹{formatRupees(opt.monthly_price * months)}
                            </span>
                          )}
                          <span className="dm-serif text-lg font-bold text-[#bc1535]">
                            ₹{formatRupees(periodTotal)}
                          </span>
                          {months > 1 && (
                            <div className="text-[10px] font-bold text-[#bc1535]">
                              (₹{formatRupees(Math.round(periodTotal / months))}/mo)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>

            {/* 04 · Paid Ad Campaigns */}
            <motion.section {...reveal}>
              <StepHeader
                num="04"
                title="Paid Ad Campaigns"
                caption="Expert campaign setup, custom lookalike audience funnels, and weekly ROAS optimization."
              />

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Meta Ads Card */}
                {metaAds.enabled && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setEnableMetaAds(!enableMetaAds)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEnableMetaAds(!enableMetaAds);
                      }
                    }}
                    className={`dm-option-card dm-card-focus flex cursor-pointer flex-col justify-between rounded-2xl border p-3 sm:p-4 ${enableMetaAds
                        ? "border-[#bc1535] bg-[#fff6f4] ring-1 ring-[#bc1535]/25 shadow-sm"
                        : "border-[#161310]/10 bg-[#fcfaf7] hover:border-[#161310]/30 hover:shadow-md"
                      }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckDot active={enableMetaAds} />
                        <div className="flex items-center gap-1.5">
                          <FaMeta className="text-blue-600 text-sm" />
                          <span className="text-sm font-bold text-[#161310]">
                            {metaAds.name}
                          </span>
                        </div>
                      </div>
                      <p className="mt-1.5 pl-6 text-xs text-[#161310]/60">
                        {metaAds.description}
                      </p>
                    </div>
                    <div className="mt-3 border-t border-[#161310]/10 pt-2 pl-6">
                      <p className="text-[10px] font-semibold text-[#161310]/55">
                        {enableMetaAds
                          ? "Selected — set your planned ad spend in the budget section below."
                          : "Tap to select — set your planned ad spend in the budget section below."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Google Ads Card */}
                {googleAds.enabled && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setEnableGoogleAds(!enableGoogleAds)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEnableGoogleAds(!enableGoogleAds);
                      }
                    }}
                    className={`dm-option-card dm-card-focus flex cursor-pointer flex-col justify-between rounded-2xl border p-3 sm:p-4 ${enableGoogleAds
                        ? "border-[#bc1535] bg-[#fff6f4] ring-1 ring-[#bc1535]/25 shadow-sm"
                        : "border-[#161310]/10 bg-[#fcfaf7] hover:border-[#161310]/30 hover:shadow-md"
                      }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckDot active={enableGoogleAds} />
                        <div className="flex items-center gap-1.5">
                          <FaGoogle className="text-red-500 text-sm" />
                          <span className="text-sm font-bold text-[#161310]">
                            {googleAds.name}
                          </span>
                        </div>
                      </div>
                      <p className="mt-1.5 pl-6 text-xs text-[#161310]/60">
                        {googleAds.description}
                      </p>
                    </div>
                    <div className="mt-3 border-t border-[#161310]/10 pt-2 pl-6">
                      <p className="text-[10px] font-semibold text-[#161310]/55">
                        {enableGoogleAds
                          ? "Selected — set your planned ad spend in the budget section below."
                          : "Tap to select — set your planned ad spend in the budget section below."}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Ad Spend — Per Day / Per Month toggle + input/slider */}
              {(metaOn || googleOn) && (
                <div className="mt-3 space-y-2.5 rounded-2xl border border-[#161310] bg-[#161310] p-3 sm:p-4 shadow-md text-white">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label
                      htmlFor="daily-ad-spend-input"
                      className="text-[10px] uppercase tracking-wider text-white/70"
                    >
                      Planned Ad Spend
                    </label>
                    {/* Per Day / Per Month toggle buttons — white background, selected brand red */}
                    <div className="inline-flex rounded-lg bg-white p-1 border border-white/20 shadow-sm">
                      {["day", "month"].map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setSpendMode(mode)}
                          className={`rounded-md px-3 py-1 text-[11px] font-bold transition-all ${spendMode === mode
                              ? "bg-[#bc1535] text-white shadow-sm"
                              : "bg-transparent text-[#161310] hover:text-[#161310]/70"
                            }`}
                        >
                          Per {mode === "day" ? "Day" : "Month"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-white">
                    Est. ₹{formatRupees(monthlyBudget)}/mo · ₹{formatRupees(periodBudget)} for {durationLongLabel} ({durationLabel})
                  </div>

                  {/* Numeric Input Box with ₹ prefix — white background with black text */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
                    <div className="relative flex items-center w-full sm:w-56">
                      <span className="pointer-events-none absolute left-3 font-bold text-sm text-[#bc1535]">
                        ₹
                      </span>
                      <input
                        id="daily-ad-spend-input"
                        type="number"
                        min={0}
                        max={spendMode === "month" ? 3000000 : 100000}
                        step={spendMode === "month" ? 1000 : 100}
                        value={displayBudget || ""}
                        onChange={(e) => handleBudgetInput(e.target.value)}
                        placeholder={spendMode === "month" ? "30000" : "1000"}
                        className="no-spinner w-full rounded-xl border border-white bg-white py-2 pl-7 pr-14 text-sm font-bold text-[#161310] outline-none transition focus:border-[#bc1535] focus:ring-2 focus:ring-[#bc1535]/30 shadow-sm"
                      />
                      <span className="pointer-events-none absolute right-3 text-[11px] font-semibold text-[#161310]/50">
                        / {spendMode === "month" ? "mo" : "day"}
                      </span>
                    </div>

                    {/* Quick Presets (pills/buttons — white background with black text) */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {budgetPresets.map((preset) => {
                        const presetValue = spendMode === "month" ? preset.daily * 30 : preset.daily;
                        const isSelected = dailyBudget === preset.daily;
                        return (
                          <button
                            key={preset.daily}
                            type="button"
                            onClick={() => setDailyBudget(preset.daily)}
                            className={`rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition-all shadow-sm ${isSelected
                                ? "border border-white/40 bg-[#bc1535] text-white ring-1 ring-white/30"
                                : "border border-transparent bg-white text-[#161310] hover:bg-white/90"
                              }`}
                          >
                            ₹{formatRupees(presetValue)}/{spendMode === "month" ? "mo" : "day"}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Budget slider */}
                  <div className="mt-2.5 px-1">
                    <input
                      type="range"
                      min={200}
                      max={10000}
                      step={100}
                      value={Math.min(dailyBudget, 10000)}
                      onChange={(e) => setDailyBudget(Number(e.target.value))}
                      aria-label="Planned ad spend slider (per day basis)"
                      className="dm-compact-slider dm-white-slider"
                      style={{
                        background: `linear-gradient(to right, #bc1535 0%, #bc1535 ${(Math.min(dailyBudget, 10000) / 10000) * 100
                          }%, #ffffff ${(Math.min(dailyBudget, 10000) / 10000) * 100}%, #ffffff 100%)`,
                      }}
                    />
                  </div>

                  <p className="text-[10px] text-white/50 italic">
                    * Ad spend budget is paid directly to Meta or Google — 0% markup by Indorinama.
                  </p>
                </div>
              )}
            </motion.section>



            {/* 05 · On-Ground Shoots & Production (Optional) */}
            <motion.section {...reveal}>
              <StepHeader
                num="05"
                title="On-Ground Shoots & Production"
                caption="Add photography, reel, podcast or drone shoots. Prices adjust with plan duration."
              />

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {shootServices.map((shoot) => {
                  const qty = selectedShoots[shoot.id] || 0;
                  const unitP = getShootPrice(shoot);
                  const baseP = getBaseShootPrice(shoot);
                  const saved = Math.max(0, baseP - unitP);
                  const isSelected = qty > 0;
                  return (
                    <div
                      key={shoot.id}
                      className={`dm-option-card flex flex-col justify-between rounded-2xl border p-3 sm:p-4 ${isSelected
                          ? "border-[#bc1535] bg-[#fff6f4] ring-1 ring-[#bc1535]/25 shadow-sm"
                          : "border-[#161310]/10 bg-[#fcfaf7] hover:border-[#161310]/30 hover:shadow-md"
                        }`}
                    >
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#bc1535]/10 text-[#bc1535]">
                              <ShootIcon icon={shoot.icon} />
                            </span>
                            <span className="text-sm font-bold text-[#161310]">{shoot.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveShootModal(shoot);
                            }}
                            title="What we are providing"
                            aria-label={`What we provide for ${shoot.name}`}
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#161310]/20 bg-white text-[10px] font-bold text-[#161310]/70 hover:border-[#bc1535] hover:bg-[#bc1535] hover:text-white transition shadow-sm"
                          >
                            i
                          </button>
                        </div>
                        {shoot.duration && (
                          <p className="mt-1 pl-10 text-[10px] font-semibold uppercase tracking-wider text-[#161310]/45">
                            {shoot.duration}
                          </p>
                        )}
                        {Array.isArray(shoot.inclusions) && shoot.inclusions.length > 0 && (
                          <ul className="mt-2 space-y-1 pl-10">
                            {shoot.inclusions.slice(0, 3).map((inc) => (
                              <li key={inc} className="flex items-start gap-1.5 text-[11px] text-[#161310]/70">
                                <FaCheck className="mt-0.5 shrink-0 text-[8px] text-[#bc1535]" />
                                <span>{inc}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="mt-3 border-t border-[#161310]/10 pt-2 pl-10">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-[#bc1535]">
                            {months > 1 ? `${months} month Total` : "Per Shoot"}
                            {months > 1 && saved > 0 && (
                              <span className="mt-0.5 block text-[10px] font-bold normal-case tracking-normal text-emerald-600">
                                (₹{formatRupees(saved)} you saved)
                              </span>
                            )}
                          </span>
                          <div className="text-right">
                            {discountPct > 0 && (
                              <span className="mr-1.5 text-xs text-[#161310]/35 line-through">
                                ₹{formatRupees(baseP)}
                              </span>
                            )}
                            <span className="dm-serif text-lg font-bold text-[#bc1535]">
                              ₹{formatRupees(unitP)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wider text-[#161310]/40 font-semibold">Qty</span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              aria-label={`Decrease ${shoot.name}`}
                              onClick={() => updateShootQty(shoot.id, -1)}
                              disabled={qty <= 0}
                              className="flex h-6 w-6 items-center justify-center rounded-md bg-[#161310] text-[#f5efe8] transition hover:bg-[#3a332c] active:scale-95 disabled:opacity-20"
                            >
                              <FaMinus className="text-[7px]" />
                            </button>
                            <span className="w-8 text-center text-xs font-bold text-[#161310]">{qty || 0}</span>
                            <button
                              type="button"
                              aria-label={`Increase ${shoot.name}`}
                              onClick={() => updateShootQty(shoot.id, 1)}
                              className="flex h-6 w-6 items-center justify-center rounded-md bg-[#161310] text-[#f5efe8] transition hover:bg-[#3a332c] active:scale-95"
                            >
                              <FaPlus className="text-[7px]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>
          </div>

          {/* ─── RIGHT: STICKY SUMMARY SIDEBAR (Desktop) ─── */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-36">
              <div className="dm-summary-shadow overflow-hidden rounded-2xl border border-[#161310]/10 bg-[#fcfaf7]">
                {/* Header */}
                <div className="bg-[#161310] px-4 py-3 text-[#f5efe8]">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#f5efe8]/50">
                      Plan Estimate
                    </span>
                    <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[9px] font-bold text-white shadow-sm">
                      {durationLabel}
                    </span>
                  </div>
                  <h3 className="dm-serif mt-1 text-xl font-normal italic tracking-tight text-[#ff8ca0]">
                    The Plan Builder
                  </h3>
                </div>

                {/* Plan Duration Slider inside Plan Estimate (Desktop) */}
                <div className="border-b border-[#161310]/10 bg-[#f5efe8]/60 p-3 sm:p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#161310]/60">
                      Plan Duration
                    </span>
                    <span className="inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-bold text-white shadow-sm">
                      {durationLabel}
                      {discountPct > 0 && ` · ${discountPct}% off`}
                    </span>
                  </div>

                  {/* Stepped Range Slider */}
                  <div className="relative my-2 px-0.5">
                    <input
                      type="range"
                      min={0}
                      max={maxIdx}
                      step={1}
                      value={sliderIndex}
                      onChange={(e) => setSliderIndex(Number(e.target.value))}
                      aria-label="Select plan duration"
                      className="dm-range-slider"
                      style={{
                        background: `linear-gradient(to right, #bc1535 0%, #bc1535 ${sliderPercentage}%, #161310 ${sliderPercentage}%, #161310 100%)`,
                      }}
                    />
                  </div>

                  {/* Stepped Labels Row */}
                  <div className="flex justify-between px-0.5 text-xs font-semibold text-[#161310]/50">
                    {durations.map((d, idx) => {
                      const isActive = sliderIndex === idx;
                      return (
                        <button
                          key={d.months}
                          type="button"
                          onClick={() => setSliderIndex(idx)}
                          className={`flex flex-col items-center transition-all ${isActive
                              ? "font-bold text-[#bc1535] scale-105"
                              : "hover:text-[#161310]"
                            }`}
                        >
                          <span>{d.label}</span>
                          {d.discount_percentage > 0 && (
                            <span className="text-[9px] font-bold text-emerald-600">
                              {d.discount_percentage}% off
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected items: names and totals stay visible; expand for pricing details. */}
                <div className="px-4 py-3 text-xs">
                  {planItems.map((item) => <PlanSummaryItem key={item.id} {...item} />)}
                  {planItems.length === 0 && (
                    <div className="rounded-xl border border-dashed border-[#161310]/20 bg-white/50 px-3 py-2 text-center text-[10px] text-[#161310]/45">
                      Add items above to customize your estimate
                    </div>
                  )}
                </div>

                {/* Math breakdown */}
                <div className="space-y-1.5 border-t border-[#161310]/10 px-4 py-2.5 text-[11px] text-[#161310]/60">
                  {selectedManagement && (
                    <>
                      <div className="flex justify-between">
                        <span>Monthly base (management)</span>
                        <span className="font-medium text-[#161310]">
                          ₹{formatRupees(mgmtMonthly)} / mo
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Duration multiplier</span>
                        <span className="font-medium text-[#161310]">× {months} months</span>
                      </div>
                    </>
                  )}
                  {totalSavings > 0 && (
                    <div className="flex justify-between font-bold text-emerald-600">
                      <span>Total Discount ({durationLongLabel} · {discountPct}% off)</span>
                      <span>− ₹{formatRupees(totalSavings)}</span>
                    </div>
                  )}
                  {creativeTotal > 0 && (
                    <div className="flex justify-between">
                      <span>Content &amp; creatives</span>
                      <span className="font-medium text-[#161310]">
                        ₹{formatRupees(creativeTotal)}
                      </span>
                    </div>
                  )}
                  {shootsTotal > 0 && (
                    <div className="flex justify-between">
                      <span>Shoots &amp; services</span>
                      <span className="font-medium text-[#161310]">
                        ₹{formatRupees(shootsTotal)}
                      </span>
                    </div>
                  )}
                  {couponApplied && couponDiscount > 0 && (
                    <div className="flex justify-between font-bold text-[#bc1535]">
                      <span>Coupon {couponCode} ({couponPct}% off)</span>
                      <span>− ₹{formatRupees(couponDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>GST (18%)</span>
                    <span className="font-medium text-[#161310]">
                      ₹{formatRupees(gstAmount)}
                    </span>
                  </div>
                </div>

                {/* Coupon input */}
                <div className="border-t border-[#161310]/10 px-4 py-2.5">
                  {!couponApplied ? (
                    <div>
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                          placeholder="Coupon code (e.g. INDORI18)"
                          className="w-full rounded-lg border border-[#161310]/15 bg-white px-2.5 py-1.5 text-[11px] font-bold uppercase text-[#161310] outline-none placeholder:font-medium placeholder:normal-case placeholder:text-[#161310]/35 focus:border-[#bc1535]"
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          className="shrink-0 rounded-lg bg-[#161310] px-3 py-1.5 text-[11px] font-bold text-white transition hover:bg-[#3a332c] disabled:opacity-60"
                        >
                          {couponLoading ? "..." : "Apply"}
                        </button>
                      </div>
                      {couponError && <p className="mt-1 text-[10px] font-semibold text-[#bc1535]">{couponError}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between rounded-lg bg-[#bc1535]/10 px-2.5 py-1.5">
                      <span className="text-[11px] font-bold text-[#bc1535]">
                        {couponCode} · {couponPct}% off applied
                      </span>
                      <button type="button" onClick={handleRemoveCoupon} className="text-[11px] font-bold text-[#161310]/60 underline hover:text-[#bc1535]">
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Grand Total */}
                <div className="flex items-end justify-between bg-[#f5efe8]/70 px-4 py-3">
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#161310]/50">
                      Total Investment
                    </div>
                    <div className="text-[9px] text-[#161310]/40">
                      Incl. 18% GST
                    </div>
                  </div>
                  <motion.span
                    key={grandTotal}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: easeOut }}
                    className="dm-serif text-2xl font-bold tracking-tight text-[#bc1535]"
                  >
                    ₹{formatRupees(grandTotal)}
                  </motion.span>
                </div>

                {months > 1 && (
                  <p className="px-4 pb-2 text-right text-xs font-semibold text-emerald-600">
                    Average ₹{formatRupees(monthlyEquivalent)}/month (incl. GST)
                  </p>
                )}

                {/* CTAs */}
                <div className="space-y-2 px-4 pb-4 pt-2">
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[#bc1535] py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#a10f2b] active:scale-[0.99] disabled:opacity-75"
                  >
                    <FaCartShopping className="text-xs" />
                    <span>{isAddingToCart ? "Adding to Cart..." : "Add to Cart"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl border border-[#bc1535]/30 bg-white py-2.5 text-xs font-bold text-[#bc1535] transition hover:bg-[#fff5f6] active:scale-[0.99]"
                  >
                    <span>Request Custom Proposal</span>
                    <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-0.5" />
                  </button>

                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#161310]/15 bg-white py-2 text-xs font-semibold text-[#161310]/75 transition hover:border-[#161310]/40 hover:bg-[#f5efe8]"
                  >
                    <FaWhatsapp className="text-sm text-emerald-600" />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>

              {/* Trust note — brand red background, white text, bigger */}
              <div className="mt-2.5 flex items-center gap-2.5 rounded-xl bg-[#bc1535] px-4 py-3 shadow-sm">
                <FaShieldAlt className="shrink-0 text-base text-white" />
                <p className="text-xs sm:text-sm font-semibold leading-snug text-white">
                  No advance commitment — adjust creatives, duration or shoots anytime before kickoff.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* ═══ MOBILE BOTTOM DRAWER / BREAKDOWN SHEET ═══ */}
      <AnimatePresence>
        {isDrawerOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#161310]/60 backdrop-blur-sm lg:hidden">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="max-h-[85vh] w-full overflow-hidden rounded-t-3xl bg-[#fcfaf7] shadow-2xl flex flex-col"
            >
              {/* Brand Black Header */}
              <div className="bg-[#161310] px-3 py-3 text-[#f5efe8] flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <h3 className="dm-serif truncate text-base font-bold text-[#f5efe8]">
                    Your Plan Breakdown
                  </h3>
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-emerald-600 px-2 py-0.5 text-[9px] font-bold text-white shadow-sm">
                    {durationLabel}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDrawerOpen(false)}
                  className="shrink-0 rounded-full p-1.5 text-[#f5efe8]/60 hover:bg-white/10 hover:text-white transition"
                >
                  <LuX className="text-lg" />
                </button>
              </div>

              <div className="overflow-y-auto px-3 py-3">
                {/* Duration Slider inside Mobile Drawer */}
                <div className="rounded-xl bg-white p-3 border border-[#161310]/10">
                  <div className="flex items-center justify-between gap-2 text-xs mb-2">
                    <span className="shrink-0 font-bold text-[#161310]/60">Duration:</span>
                    <span className="whitespace-nowrap font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full text-[11px] shadow-sm">
                      {durationLabel} {discountPct > 0 && `(${discountPct}% off)`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={maxIdx}
                    step={1}
                    value={sliderIndex}
                    onChange={(e) => setSliderIndex(Number(e.target.value))}
                    aria-label="Adjust plan duration"
                    className="dm-compact-slider"
                    style={{
                      background: `linear-gradient(to right, #bc1535 0%, #bc1535 ${sliderPercentage}%, #161310 ${sliderPercentage}%, #161310 100%)`,
                    }}
                  />
                  <div className="mt-1.5 flex justify-between text-[10px] font-medium text-[#161310]/50">
                    {durations.map((d, idx) => (
                      <span
                        key={`drawer-lbl-${d.months}`}
                        className={sliderIndex === idx ? "font-bold text-[#bc1535]" : ""}
                        onClick={() => setSliderIndex(idx)}
                      >
                        {d.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Selected items in the mobile plan summary. */}
                <div className="mt-4 text-xs">
                  {planItems.map((item) => <PlanSummaryItem key={item.id} {...item} />)}
                </div>

                {/* Total & Action Buttons */}
                <div className="mt-5 border-t border-[#161310]/15 pt-3">
                  {totalSavings > 0 && (
                    <div className="mb-1.5 flex justify-between text-[11px] font-bold text-emerald-600">
                      <span>Total Discount ({durationLongLabel} · {discountPct}% off)</span>
                      <span>− ₹{formatRupees(totalSavings)}</span>
                    </div>
                  )}
                  {couponApplied && couponDiscount > 0 && (
                    <div className="mb-1.5 flex justify-between text-[11px] font-bold text-[#bc1535]">
                      <span>Coupon {couponCode} ({couponPct}%)</span>
                      <span>− ₹{formatRupees(couponDiscount)}</span>
                    </div>
                  )}
                  <div className="mb-1.5 flex justify-between text-[11px] text-[#161310]/60">
                    <span>GST (18%)</span>
                    <span className="font-medium text-[#161310]">₹{formatRupees(gstAmount)}</span>
                  </div>
                  {!couponApplied ? (
                    <div className="mb-2.5 flex gap-1.5">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Coupon code"
                        className="w-full rounded-lg border border-[#161310]/15 bg-white px-2.5 py-1.5 text-[11px] font-bold uppercase text-[#161310] outline-none placeholder:font-medium placeholder:normal-case placeholder:text-[#161310]/35 focus:border-[#bc1535]"
                      />
                      <button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="shrink-0 rounded-lg bg-[#161310] px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-60"
                      >
                        {couponLoading ? "..." : "Apply"}
                      </button>
                    </div>
                  ) : (
                    <div className="mb-2.5 flex items-center justify-between rounded-lg bg-[#bc1535]/10 px-2.5 py-1.5">
                      <span className="text-[11px] font-bold text-[#bc1535]">{couponCode} applied</span>
                      <button type="button" onClick={handleRemoveCoupon} className="text-[11px] font-bold text-[#161310]/60 underline">
                        Remove
                      </button>
                    </div>
                  )}
                  {couponError && !couponApplied && <p className="mb-2 text-[10px] font-semibold text-[#bc1535]">{couponError}</p>}
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#161310]/50">
                        Grand Total (incl. GST)
                      </span>
                    </div>
                    <span className="dm-serif text-2xl font-bold text-[#bc1535]">
                      ₹{formatRupees(grandTotal)}
                    </span>
                  </div>

                  {months > 1 && (
                    <p className="mb-3 text-right text-xs font-semibold text-emerald-600">
                      Average ₹{formatRupees(monthlyEquivalent)}/month (incl. GST)
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={isAddingToCart}
                      className="flex items-center justify-center gap-1.5 rounded-xl bg-[#bc1535] py-2.5 text-xs font-bold text-white shadow-sm"
                    >
                      <FaCartShopping className="text-xs" />
                      <span>{isAddingToCart ? "Adding..." : "Add to Cart"}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setIsDrawerOpen(false);
                        setIsModalOpen(true);
                      }}
                      className="flex items-center justify-center gap-1 rounded-xl border border-[#bc1535] py-2.5 text-xs font-bold text-[#bc1535] bg-white"
                    >
                      <span>Get Proposal</span>
                      <FaArrowRight className="text-[10px]" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══ WHAT WE PROVIDE (i) MODAL (Brand Black Header) ═══ */}
      <AnimatePresence>
        {activeInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#161310]/70 p-3 sm:p-4 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-3xl bg-[#fcfaf7] shadow-2xl border border-[#161310]/10 flex flex-col"
            >
              {/* Brand Black Header */}
              <div className="bg-[#161310] px-6 py-4 text-[#f5efe8] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#bc1535] text-white">
                    <ContentTypeIcon
                      typeKey={activeInfoModal.id || activeInfoModal.icon}
                      className="text-lg text-white"
                    />
                  </div>
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#ff8ca0]">
                      Deliverables &amp; Scope
                    </span>
                    <h3 className="dm-serif text-lg sm:text-xl font-normal text-[#f5efe8]">
                      {activeInfoModal.name}
                    </h3>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveInfoModal(null)}
                  aria-label="Close details"
                  className="rounded-full p-1.5 text-[#f5efe8]/60 hover:bg-white/10 hover:text-white transition"
                >
                  <LuX className="text-lg" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto p-5 sm:p-6 space-y-4">
                {/* YouTube Video Player (or showcase preview) */}
                {(() => {
                  const embedUrl = activeInfoModal ? getYouTubeEmbedUrl(activeInfoModal.youtube_video_url) : null;
                  if (embedUrl) {
                    return (
                      <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-video shadow-md border border-[#161310]/15">
                        <iframe
                          src={embedUrl}
                          title={`${activeInfoModal.name} preview video`}
                          className="absolute inset-0 h-full w-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    );
                  }
                  return (
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#161310]/20 bg-white p-6 text-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#bc1535]/10 text-[#bc1535] mb-2">
                        <LuFilm className="text-xl" />
                      </div>
                      <span className="text-xs font-semibold text-[#161310]">Sample Showcase Video</span>
                      <span className="text-[11px] text-[#161310]/50 mt-0.5">Sample video coming soon</span>
                    </div>
                  );
                })()}

                {/* Description */}
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#bc1535] mb-1">
                    Deliverable Overview
                  </h4>
                  <p className="text-xs leading-relaxed text-[#161310]/80">
                    {activeInfoModal.description}
                  </p>
                </div>

                {activeInfoModal.specifications && (
                  <div className="rounded-xl border border-[#161310]/10 bg-white p-3 text-xs">
                    <span className="font-bold text-[#161310]">Specifications &amp; Format:</span>
                    <p className="mt-0.5 text-[11px] text-[#161310]/65">
                      {activeInfoModal.specifications}
                    </p>
                  </div>
                )}

                {activeInfoModal.info_points && activeInfoModal.info_points.length > 0 && (
                  <div className="border-t border-[#161310]/10 pt-3">
                    <h5 className="text-[11px] font-bold text-[#161310] mb-2">
                      {activeInfoModal.info_section_heading || "What we are providing:"}
                    </h5>
                    <ul className="space-y-1.5">
                      {activeInfoModal.info_points.map((pt, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-[#161310]/80">
                          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                            <LuCheck className="text-[8px]" />
                          </span>
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between border-t border-[#161310]/10 pt-4">
                  <div className="flex items-baseline gap-1.5">
                    {discountPct > 0 && (
                      <span className="text-xs text-[#161310]/35 line-through">
                        ₹{formatRupees(getBaseUnitPrice(activeInfoModal.id))}
                      </span>
                    )}
                    <span className="dm-serif text-xl font-bold text-[#bc1535]">
                      ₹{formatRupees(getUnitPrice(activeInfoModal.id))}
                    </span>
                    <span className="text-[10px] text-[#161310]/50">
                      /{activeInfoModal.unit} ({durationLabel})
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveInfoModal(null)}
                    className="rounded-full bg-[#161310] px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-[#3a332c]"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══ SHOOT DETAILS (i) MODAL — same pattern as content types ═══ */}
      <AnimatePresence>
        {activeShootModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#161310]/70 p-3 sm:p-4 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-3xl bg-[#fcfaf7] shadow-2xl border border-[#161310]/10 flex flex-col"
            >
              <div className="bg-[#161310] px-6 py-4 text-[#f5efe8] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#bc1535] text-white">
                    <ShootIcon icon={activeShootModal.icon} />
                  </div>
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#ff8ca0]">
                      {activeShootModal.info_modal_title || "Deliverables & Scope"}
                    </span>
                    <h3 className="dm-serif text-lg sm:text-xl font-normal text-[#f5efe8]">
                      {activeShootModal.name}
                    </h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveShootModal(null)}
                  aria-label="Close shoot details"
                  className="rounded-full p-1.5 text-[#f5efe8]/60 hover:bg-white/10 hover:text-white transition"
                >
                  <LuX className="text-lg" />
                </button>
              </div>

              <div className="overflow-y-auto p-5 sm:p-6 space-y-4">
                {(() => {
                  const embedUrl = activeShootModal ? getYouTubeEmbedUrl(activeShootModal.youtube_video_url) : null;
                  if (embedUrl) {
                    return (
                      <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-video shadow-md border border-[#161310]/15">
                        <iframe
                          src={embedUrl}
                          title={`${activeShootModal.name} preview video`}
                          className="absolute inset-0 h-full w-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    );
                  }
                  return null;
                })()}

                {(activeShootModal.description || activeShootModal.duration) && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#bc1535] mb-1">
                      Deliverable Overview
                    </h4>
                    {activeShootModal.description && (
                      <p className="text-xs leading-relaxed text-[#161310]/80">
                        {activeShootModal.description}
                      </p>
                    )}
                    {activeShootModal.duration && (
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[#161310]/50">
                        {activeShootModal.duration}
                      </p>
                    )}
                  </div>
                )}

                {activeShootModal.specifications && (
                  <div className="rounded-xl border border-[#161310]/10 bg-white p-3 text-xs">
                    <span className="font-bold text-[#161310]">Specifications &amp; Format:</span>
                    <p className="mt-0.5 text-[11px] text-[#161310]/65">
                      {activeShootModal.specifications}
                    </p>
                  </div>
                )}

                {activeShootModal.info_points && activeShootModal.info_points.length > 0 && (
                  <div className="border-t border-[#161310]/10 pt-3">
                    <h5 className="text-[11px] font-bold text-[#161310] mb-2">
                      {activeShootModal.info_section_heading || "What we are providing:"}
                    </h5>
                    <ul className="space-y-1.5">
                      {activeShootModal.info_points.map((pt, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-[#161310]/80">
                          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                            <LuCheck className="text-[8px]" />
                          </span>
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {activeShootModal.note && (
                  <div className="rounded-xl bg-[#f5efe8] p-3 text-[11px] italic text-[#161310]/60">
                    Note: {activeShootModal.note}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between border-t border-[#161310]/10 pt-4">
                  <div className="flex items-baseline gap-1.5">
                    {discountPct > 0 && (
                      <span className="text-xs text-[#161310]/35 line-through">
                        ₹{formatRupees(getBaseShootPrice(activeShootModal))}
                      </span>
                    )}
                    <span className="dm-serif text-xl font-bold text-[#bc1535]">
                      ₹{formatRupees(getShootPrice(activeShootModal))}
                    </span>
                    <span className="text-[10px] text-[#161310]/50">
                      /shoot ({durationLabel})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveShootModal(null)}
                    className="rounded-full bg-[#161310] px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-[#3a332c]"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══ REQUEST PROPOSAL MODAL (Brand Black Header) ═══ */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#161310]/70 p-3 sm:p-4 backdrop-blur-sm">
          <div className="relative max-h-[92vh] w-full max-w-lg overflow-hidden rounded-3xl bg-[#fcfaf7] shadow-2xl flex flex-col">
            {/* Brand Black Header */}
            <div className="bg-[#161310] px-6 py-4 text-[#f5efe8] flex items-center justify-between">
              <div>
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#ff8ca0]">
                  Customized Growth Proposal
                </span>
                <h3 className="dm-serif mt-0.5 text-xl font-light text-[#f5efe8]">
                  Receive Your Proposal &amp; Schedule Kickoff
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close proposal form"
                className="rounded-full p-1.5 text-[#f5efe8]/60 hover:bg-white/10 hover:text-white transition"
              >
                <LuX className="text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-5 sm:p-6">
              <p className="mb-3 text-xs text-[#161310]/60">
                Review your configuration below. Our strategy directors will review your scope and contact you within 2 business hours.
              </p>

              {/* Full scope is collapsed by default and resets when the modal reopens. */}
              <details className="dm-plan-item mb-4 rounded-2xl border border-[#161310]/10 bg-white p-3.5 text-xs">
                <summary className="dm-plan-item-summary">
                  <span className="min-w-0 flex-1 font-bold text-[#161310]">
                    {durationLabel} Plan ({months} Months)
                    <span className="mt-1 block text-[11px] font-normal text-[#161310]/60">
                      View full plan details
                    </span>
                  </span>
                  <span className="shrink-0 font-bold text-[#bc1535]">
                    ₹{formatRupees(grandTotal)}
                    <span className="block text-right text-[10px] font-normal">incl. GST</span>
                  </span>
                  <LuChevronUp aria-hidden="true" className="dm-plan-item-chevron shrink-0 text-[#bc1535]" />
                </summary>
                <ul className="mt-3 space-y-2 border-t border-[#161310]/10 pt-3 text-[11px] leading-relaxed text-[#161310]/70">
                  {planDetails.map((detail, index) => (
                    <li key={index} className="break-words whitespace-pre-wrap">{detail}</li>
                  ))}
                </ul>
              </details>

              <form onSubmit={handleSubmitProposal} className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[#161310]/70">
                    Your Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rohit Verma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none transition focus:border-[#bc1535]"
                  />
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-[#161310]/70">
                      Phone / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none transition focus:border-[#bc1535]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-[#161310]/70">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none transition focus:border-[#bc1535]"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-[#161310]/70">
                    Company / Brand Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Verma Retail Pvt Ltd"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none transition focus:border-[#bc1535]"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-[#161310]/70">
                    Additional Notes or Questions
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Anything specific you want to focus on (e.g. reel pacing, target pin codes, specific shoot dates)..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none transition focus:border-[#bc1535]"
                  />
                </div>

                <div className="mt-4 space-y-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#bc1535] py-2.5 text-xs font-bold text-white shadow-md transition hover:bg-[#a10f2b] disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <span>Submitting proposal request...</span>
                    ) : (
                      <span>Submit &amp; Request Callback</span>
                    )}
                  </button>

                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#161310]/15 bg-white py-2 text-xs font-semibold text-[#161310] hover:bg-[#f5efe8]"
                  >
                    <FaWhatsapp className="text-emerald-600 text-sm" />
                    <span>Or Chat Instantly on WhatsApp</span>
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REGISTER MODAL (for unauthenticated add to cart) ═══ */}
      {regModal && <RegisterModal
        isOpen={regModal}
        toggle={() => { pendingCartPlan.current = null; setRegModal(false); }}
        onLoginSuccess={() => setRegModal(false)}
      />}

      {/* ═══ OTHER SERVICES (Box Pattern with Full Details Page Link) ═══ */}
      <section id="other-services" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 border-t border-[#161310]/10">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#bc1535]">
            Specialized Add-ons &amp; Production
          </span>
          <h2 className="dm-serif mt-2 text-2xl font-light tracking-tight text-[#161310] sm:text-3xl md:text-4xl">
            Other <em className="font-normal italic text-[#bc1535]">Services</em>
          </h2>
          <p className="mt-2 text-xs sm:text-sm text-[#161310]/65">
            Expand your brand reach with on-ground video shoots, influencer partnerships, broadcast messaging, and verified B2B lead generation.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3">
          {otherServices.map((srv) => {
            const isInfluencer = srv.slug === "influencer" || srv.slug === "influencer-marketing";
            const targetUrl = isInfluencer ? "/influencer-marketing" : `/digital-marketing/services/${srv.slug}`;
            return (
              <Link
                key={srv.slug}
                to={targetUrl}
                className="group relative flex flex-col justify-between rounded-2xl border border-[#161310] bg-[#161310] p-4 sm:p-5 text-left transition hover:border-[#bc1535] hover:shadow-[0_18px_40px_-16px_rgba(188,21,53,0.55)] no-underline"
              >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#bc1535] text-white transition shadow-sm group-hover:bg-[#d31a40]">
                    {(srv.slug === "shoots" || srv.icon === "camera") && <FaCamera className="text-lg" />}
                    {(srv.slug === "influencer" || srv.slug === "influencer-marketing") && <FaStar className="text-lg" />}
                    {(srv.slug === "whatsapp" || srv.slug === "whatsapp-marketing") && <FaWhatsapp className="text-lg" />}
                    {(srv.slug === "mail" || srv.slug === "email-marketing" || srv.slug === "email") && <FaEnvelope className="text-lg" />}
                    {(srv.slug === "sms" || srv.slug === "bulk-sms" || srv.slug === "sms-marketing") && <FaComments className="text-lg" />}
                    {(srv.slug === "google-data-extractor" || srv.slug === "data-extractor") && <FaGoogle className="text-lg" />}
                    {(srv.slug === "static-website" || srv.slug === "website" || srv.icon === "website") && <FaGlobe className="text-lg" />}
                  </div>

                  {srv.badge && (
                    <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/70 group-hover:bg-[#bc1535]/20 group-hover:text-[#ff8ca0] transition">
                      {srv.badge}
                    </span>
                  )}
                </div>

                <h3 className="font-bold text-sm sm:text-base text-white group-hover:text-[#ff8ca0] transition">
                  {srv.title}
                </h3>

                <p className="mt-1.5 text-xs text-white/60 line-clamp-2 leading-relaxed">
                  {srv.short_description}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                <div>
                  <span className="block text-[9px] uppercase tracking-wider text-white/40">
                    Starting At
                  </span>
                  <span className="text-xs font-bold text-[#ff8ca0]">
                    {srv.starting_price || "Explore Details"}
                  </span>
                </div>

                <span className="flex items-center gap-1 text-xs font-bold text-white group-hover:text-[#ff8ca0] transition">
                  <span>Details</span>
                  <FaArrowRight className="text-[10px] transform group-hover:translate-x-1 transition" />
                </span>
              </div>
            </Link>
          );
        })}
        </div>
      </section>

      {/* ═══ AD GALLERY & PROOF ═══ */}
      <MetaAdLibrarySection />

      {/* ═══ CLIENTS & TESTIMONIALS ═══ */}
      <Clients />

      <DMShowcase data={cmsData} mediaOrigin={cmsMediaOrigin} />
      <Videos category="digital-marketing" embedded
        title={cmsData?.expertise_videos_title || "See our expertise in action"}
        detailed
        eyebrow={cmsData?.expertise_videos_eyebrow ?? "Behind the work"}
        introduction={cmsData?.expertise_videos_intro ?? "Take a closer look at our creative work, from the initial idea to the finished content. Watch the videos and read about the approach behind each project."}
        configuredVideos={cmsData?.expertise_videos || []}
      />

      <Footer />
    </div>
  );
};

export default DMHomePage;
